import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Plus, Calendar, Users, AlertTriangle, Bell, Trash2, Edit, Briefcase, UserCheck, Link as LinkIcon, ChevronLeft, ChevronRight, LogOut, Home, GanttChartSquare, UserCog, ListTodo, X, User as UserIcon, CalendarDays, Upload, Download, PieChart as PieChartIcon, BarChart3, Target, ChevronDown, MoreVertical, LayoutDashboard, Copy } from 'lucide-react';

// --- RECHARTS SCRIPT LOADER ---
const useScript = (url, id) => {
  const [isLoaded, setIsLoaded] = useState(false);
  useEffect(() => {
    if (window.Recharts) {
        setIsLoaded(true);
        return;
    }
    const script = document.createElement('script');
    script.src = url;
    script.id = id;
    script.async = true;
    script.onload = () => setIsLoaded(true);
    document.body.appendChild(script);
    return () => {
      const scriptElement = document.getElementById(id);
      if (scriptElement && scriptElement.parentNode) {
        scriptElement.parentNode.removeChild(scriptElement);
      }
    };
  }, [url, id]);
  return isLoaded;
};


// --- FIREBASE IMPORTS ---
// IMPORTANT: This is a placeholder. Replace with your actual Firebase config.
const firebaseConfig = {
  apiKey: "AIzaSyDqnaXTetNr4BfmwAC1cxFiWecp2TPHOSs",
  authDomain: "pmp-proj.firebaseapp.com",
  projectId: "pmp-proj",
  storageBucket: "pmp-proj.firebasestorage.app",
  messagingSenderId: "495400454887",
  appId: "1:495400454887:web:b3a433eea0bf3d8c4e3042",
  measurementId: "G-S2NW0W8VFJ"
};

// --- DATA & CONFIGURATION ---

const ROLES = {
  Developer: { color: 'blue', bgColor: 'bg-blue-500', lightBgColor: 'bg-blue-100 dark:bg-blue-900/30' },
  Designer: { color: 'purple', bgColor: 'bg-purple-500', lightBgColor: 'bg-purple-100 dark:bg-purple-900/30' },
  Manager: { color: 'green', bgColor: 'bg-green-500', lightBgColor: 'bg-green-100 dark:bg-green-900/30' },
  QA: { color: 'orange', bgColor: 'bg-orange-500', lightBgColor: 'bg-orange-100 dark:bg-orange-900/30' },
  TA: { color: 'teal', bgColor: 'bg-teal-500', lightBgColor: 'bg-teal-100 dark:bg-teal-900/30' },
  Default: { color: 'gray', bgColor: 'bg-gray-500', lightBgColor: 'bg-gray-100 dark:bg-gray-700/50' },
};

const PRIORITY_STYLES = {
    Low: 'bg-gray-400 text-white',
    Medium: 'bg-blue-500 text-white',
    High: 'bg-yellow-500 text-black',
    Critical: 'bg-red-600 text-white',
};
const PRIORITY_ORDER = { Critical: 4, High: 3, Medium: 2, Low: 1 };

const STATUS_STYLES = {
    'TODO': { badge: 'bg-gray-500 text-white', timeline: '' },
    'In Progress': { badge: 'bg-yellow-500 text-black', timeline: 'opacity-75' },
    'Complete': { badge: 'bg-green-500 text-white', timeline: 'opacity-50 line-through' },
};


const DAILY_WORK_HOURS = 8;

// --- HELPER FUNCTIONS ---

const dayDiff = (date1, date2) => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  if (isNaN(d1) || isNaN(d2)) return 0;
  const d1UTC = Date.UTC(d1.getFullYear(), d1.getMonth(), d1.getDate());
  const d2UTC = Date.UTC(d2.getFullYear(), d2.getMonth(), d2.getDate());
  return Math.floor((d2UTC - d1UTC) / (1000 * 60 * 60 * 24));
};

const getWorkingDays = (startDate, endDate, leave = [], holidays = []) => {
    if (!startDate || !endDate) return 0;
    let count = 0;
    const curDate = new Date(startDate);
    const lastDate = new Date(endDate);
    const leaveArray = Array.isArray(leave) ? leave : [];

    while (curDate <= lastDate) {
        const dayOfWeek = curDate.getDay();
        const isWeekend = (dayOfWeek === 6) || (dayOfWeek === 0);
        
        const dateString = curDate.toISOString().split('T')[0];
        const isHoliday = holidays.includes(dateString);

        const onLeave = leaveArray.some(period => {
            if (!period || !period.startDate || !period.endDate) return false;
            const leaveStart = new Date(period.startDate);
            const leaveEnd = new Date(period.endDate);
            return curDate >= leaveStart && curDate <= leaveEnd;
        });
        if (!isWeekend && !onLeave && !isHoliday) {
            count++;
        }
        curDate.setDate(curDate.getDate() + 1);
    }
    return count;
};

// --- AUTHENTICATION COMPONENT ---
const ReCaptcha = ({ onVerify }) => {
    const recaptchaContainer = useRef(null);
    const widgetIdRef = useRef(null);

    useEffect(() => {
        const renderRecaptcha = () => {
            if (recaptchaContainer.current && widgetIdRef.current === null) {
                widgetIdRef.current = window.grecaptcha.render(recaptchaContainer.current, {
                    'sitekey': '6LcjjXkrAAAAANfmpZuSFcbICBnH7DdSFUec-GIU', // v2 Test Key
                    'callback': onVerify,
                });
            }
        };

        if (window.grecaptcha && window.grecaptcha.render) {
            renderRecaptcha();
        } else {
            window.onRecaptchaLoadCallback = renderRecaptcha;
            if (!document.getElementById('recaptcha-script')) {
                const script = document.createElement('script');
                script.id = 'recaptcha-script';
                script.src = 'https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoadCallback&render=explicit';
                script.async = true;
                script.defer = true;
                document.body.appendChild(script);
            }
        }

        return () => {
            window.onRecaptchaLoadCallback = null;
        };
    }, [onVerify]);

    return <div ref={recaptchaContainer} className="flex justify-center my-4"></div>;
};


const AuthScreen = ({ auth }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isLogin, setIsLogin] = useState(true);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    
    const BYPASS_RECAPTCHA_FOR_TESTING = false;
    const [isRecaptchaVerified, setIsRecaptchaVerified] = useState(BYPASS_RECAPTCHA_FOR_TESTING);

    const handleRecaptcha = useCallback((token) => {
        if (token) {
            setIsRecaptchaVerified(true);
        }
    }, []);

    const handleAuth = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        const userAgent = navigator.userAgent.toLowerCase();
        const forbiddenAgents = ['bot', 'crawl', 'spider', 'slurp'];
        if (forbiddenAgents.some(agent => userAgent.includes(agent))) {
            setError('Automated access is not allowed.');
            setLoading(false);
            return;
        }

        if (!isRecaptchaVerified) {
            setError('Please complete the reCAPTCHA verification.');
            setLoading(false);
            return;
        }

        try {
            if (isLogin) {
                await auth.signInWithEmailAndPassword(email, password);
            } else {
                await auth.createUserWithEmailAndPassword(email, password);
            }
        } catch (err) {
            setError(err.message);
            if(window.grecaptcha && !BYPASS_RECAPTCHA_FOR_TESTING) {
                window.grecaptcha.reset();
            }
            setIsRecaptchaVerified(BYPASS_RECAPTCHA_FOR_TESTING);
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleSignIn = async () => {
        setLoading(true);
        setError('');
        const provider = new window.firebase.auth.GoogleAuthProvider();
        try {
            const result = await auth.signInWithPopup(provider);
            const email = result.user.email;

            const methods = await auth.fetchSignInMethodsForEmail(email);

            if (methods.length > 0 && !methods.includes('google.com')) {
                await auth.signOut();
                setError('An account already exists with this email. Please sign in with your password first.');
                setLoading(false);
                return;
            }
        } catch (err) {
            if (err.code === 'auth/account-exists-with-different-credential') {
                setError('An account already exists with this email. Please sign in with your original method to link your Google account.');
            } else {
                setError(err.message);
            }
        } finally {
            setLoading(false);
        }
    };
    
    const handleGameCenterSignIn = async () => {
        setLoading(true);
        setError('Game Center sign-in is not supported on web browsers. This is for native iOS/macOS apps.');
        setLoading(false);
    };

    return (
        <div className="flex items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900">
            <div className="w-full max-w-md p-8 space-y-6 bg-white rounded-lg shadow-md dark:bg-gray-800">
                <div>
                    <h2 className="text-3xl font-extrabold text-center text-gray-900 dark:text-white">
                        Quick Project Management
                    </h2>
                     <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
                        {isLogin ? 'Sign in to your account' : 'Create a new account'}
                    </p>
                </div>
                
                <div className="space-y-4">
                    <button onClick={handleGoogleSignIn} disabled={loading} className="w-full flex justify-center items-center gap-2 py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600">
                        <svg className="w-5 h-5" aria-hidden="true" focusable="false" data-prefix="fab" data-icon="google" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 488 512"><path fill="currentColor" d="M488 261.8C488 403.3 391.1 504 248 504 110.8 504 0 393.2 0 256S110.8 8 248 8c66.8 0 126 21.2 172.9 60.3l-66.8 64.2C314.5 99.8 283.3 80 248 80c-82.6 0-150.2 67.6-150.2 150.2s67.6 150.2 150.2 150.2c90.1 0 132.3-62.2 135.8-95.2H248v-65.4h239.2c1.2 6.4 4.3 12.7 4.3 22.6z"></path></svg>
                        Sign in with Google
                    </button>
                    <button onClick={handleGameCenterSignIn} disabled={loading} className="w-full flex justify-center items-center gap-2 py-2 px-4 border border-transparent rounded-md shadow-sm bg-blue-500 text-sm font-medium text-white hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700">
                         <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z"/></svg>
                        Sign in with Game Center
                    </button>
                </div>

                <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                        <span className="px-2 bg-white text-gray-500 dark:bg-gray-800 dark:text-gray-400">Or continue with</span>
                    </div>
                </div>

                <form className="space-y-6" onSubmit={handleAuth}>
                    <div className="rounded-md shadow-sm -space-y-px">
                        <div>
                            <input id="email-address" name="email" type="email" autoComplete="email" required
                                className="relative block w-full px-3 py-2 text-gray-900 placeholder-gray-500 border border-gray-300 rounded-none appearance-none rounded-t-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} />
                        </div>
                        <div>
                            <input id="password" name="password" type="password" autoComplete="current-password" required
                                className="relative block w-full px-3 py-2 text-gray-900 placeholder-gray-500 border border-gray-300 rounded-none appearance-none rounded-b-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
                        </div>
                    </div>

                    {!BYPASS_RECAPTCHA_FOR_TESTING && <ReCaptcha onVerify={handleRecaptcha} />}

                    {error && <p className="mt-2 text-center text-sm text-red-600 dark:text-red-400">{error}</p>}

                    <div>
                        <button type="submit" disabled={loading || !isRecaptchaVerified}
                            className="relative flex justify-center w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md group hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-300 disabled:cursor-not-allowed">
                            {loading ? 'Processing...' : (isLogin ? 'Sign in' : 'Sign up')}
                        </button>
                    </div>
                </form>
                <div className="text-sm text-center">
                    <button onClick={() => setIsLogin(!isLogin)} className="font-medium text-blue-600 hover:text-blue-500">
                        {isLogin ? 'Need an account? Sign up' : 'Already have an account? Sign in'}
                    </button>
                </div>
            </div>
        </div>
    );
};


// --- CORE COMPONENTS ---

const Modal = ({ children, onClose }) => (
  <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4">
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-lg relative overflow-y-auto max-h-full">
      <button onClick={onClose} className="absolute top-3 right-3 text-gray-500 hover:text-gray-800 dark:hover:text-gray-200">
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
      </button>
      {children}
    </div>
  </div>
);

const DataForm = ({ type, onSave, onCancel, initialData, projects, resources }) => {
  const [formData, setFormData] = useState(initialData || { leave: [], assignedResources: [], reminder: 'none', priority: 'Medium', tshirtSize: 'M' });

  const handleChange = (e) => {
    const { name, value, type: inputType } = e.target;
    setFormData(prev => ({ ...prev, [name]: inputType === 'number' ? parseInt(value) || 0 : value }));
  };

  const handleLeaveChange = (e, index) => {
    const { name, value } = e.target;
    const newLeave = [...formData.leave];
    newLeave[index] = { ...newLeave[index], [name]: value };
    setFormData(prev => ({ ...prev, leave: newLeave }));
  };

  const addLeavePeriod = () => {
    setFormData(prev => ({ ...prev, leave: [...(prev.leave || []), { startDate: '', endDate: '' }] }));
  };

  const removeLeavePeriod = (index) => {
    setFormData(prev => ({ ...prev, leave: prev.leave.filter((_, i) => i !== index) }));
  };
  
  const handleAssignedResourceChange = (index, field, value) => {
      const newAssigned = [...formData.assignedResources];
      newAssigned[index] = { ...newAssigned[index], [field]: value };
      setFormData(prev => ({ ...prev, assignedResources: newAssigned }));
  };
  
  const addAssignedResource = () => {
      const newAssigned = [...(formData.assignedResources || []), { resourceId: '', weight: 0 }];
      setFormData(prev => ({ ...prev, assignedResources: newAssigned }));
  };

  const removeAssignedResource = (index) => {
      setFormData(prev => ({ ...prev, assignedResources: prev.assignedResources.filter((_, i) => i !== index) }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ ...formData, id: initialData?.id || null });
  };

  const commonInputClass = "w-full p-2 border rounded bg-gray-50 dark:bg-gray-700 dark:border-gray-600 mb-4";

  return (
    <form onSubmit={handleSubmit}>
      <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-gray-100">{initialData?.id ? 'Edit' : 'Add'} {type}</h2>
      {type === 'Project' && <>
        <input name="name" value={formData.name || ''} onChange={handleChange} placeholder="Project Name" className={commonInputClass} required />
        <div className="flex gap-4">
            <select name="priority" value={formData.priority || 'Medium'} onChange={handleChange} className={commonInputClass}>
                {Object.keys(PRIORITY_STYLES).map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <select name="tshirtSize" value={formData.tshirtSize || 'M'} onChange={handleChange} className={commonInputClass}>
                {['XS', 'S', 'M', 'L', 'XL', 'XXL'].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
        </div>
        <div className="flex gap-4"><input type="date" name="startDate" value={formData.startDate || ''} onChange={handleChange} className={commonInputClass} required /><input type="date" name="endDate" value={formData.endDate || ''} onChange={handleChange} className={commonInputClass} required /></div>
      </>}
      {type === 'Resource' && <>
        <input name="name" value={formData.name || ''} onChange={handleChange} placeholder="Resource Name" className={commonInputClass} required />
        <select name="role" value={formData.role || ''} onChange={handleChange} className={commonInputClass} required>
          <option value="">Select Role</option>
          {Object.keys(ROLES).filter(r => r !== 'Default').map(role => <option key={role} value={role}>{role}</option>)}
        </select>
        <fieldset className="border p-4 rounded-md dark:border-gray-600 space-y-3">
            <legend className="px-2 text-sm font-medium text-gray-600 dark:text-gray-300">Leave Periods</legend>
            {formData.leave?.map((leavePeriod, index) => (
                <div key={index} className="flex items-center gap-2">
                    <input type="date" name="startDate" value={leavePeriod.startDate || ''} onChange={(e) => handleLeaveChange(e, index)} className={commonInputClass + ' mb-0'} />
                    <span className="text-gray-500">-</span>
                    <input type="date" name="endDate" value={leavePeriod.endDate || ''} onChange={(e) => handleLeaveChange(e, index)} className={commonInputClass + ' mb-0'} />
                    <button type="button" onClick={() => removeLeavePeriod(index)} className="p-2 text-red-500 hover:text-red-700 dark:hover:text-red-300 rounded-full hover:bg-red-100 dark:hover:bg-red-900/50">
                        <Trash2 size={16} />
                    </button>
                </div>
            ))}
            <button type="button" onClick={addLeavePeriod} className="text-sm font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 mt-2 flex items-center gap-1">
                <Plus size={14}/> Add Leave
            </button>
        </fieldset>
      </>}
      {type === 'Task' && <>
        <input name="name" value={formData.name || ''} onChange={handleChange} placeholder="Task Name" className={commonInputClass} required />
        <div className="flex gap-4">
            <select name="priority" value={formData.priority || 'Medium'} onChange={handleChange} className={commonInputClass}>
                {Object.keys(PRIORITY_STYLES).map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <select name="status" value={formData.status || 'TODO'} onChange={handleChange} className={commonInputClass}>
                <option value="TODO">TODO</option>
                <option value="In Progress">In Progress</option>
                <option value="Complete">Complete</option>
            </select>
        </div>
        <select name="projectId" value={formData.projectId || ''} onChange={handleChange} className={commonInputClass} required>
            <option value="">Select Project</option>{projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        
        <fieldset className="border p-4 rounded-md dark:border-gray-600 space-y-3 mb-4">
            <legend className="px-2 text-sm font-medium text-gray-600 dark:text-gray-300">Assigned Resources</legend>
            {(formData.assignedResources || []).map((assignment, index) => {
                const assignedResourceIds = formData.assignedResources.map(ar => ar.resourceId);
                return (
                    <div key={index} className="flex items-center gap-2">
                        <select 
                            value={assignment.resourceId}
                            onChange={(e) => handleAssignedResourceChange(index, 'resourceId', e.target.value)}
                            className={commonInputClass + ' mb-0 flex-grow'}
                        >
                            <option value="">Select Resource</option>
                            {resources.map(r => (
                                <option key={r.id} value={r.id} disabled={assignedResourceIds.includes(r.id) && assignment.resourceId !== r.id}>
                                    {r.name}
                                </option>
                            ))}
                        </select>
                        <input 
                            type="number" 
                            value={assignment.weight}
                            onChange={(e) => handleAssignedResourceChange(index, 'weight', e.target.value)}
                            className={commonInputClass + ' mb-0 w-24'}
                            placeholder="Weight %"
                            max="100"
                            min="0"
                        />
                        <button type="button" onClick={() => removeAssignedResource(index)} className="p-2 text-red-500 hover:text-red-700 dark:hover:text-red-300 rounded-full hover:bg-red-100 dark:hover:bg-red-900/50">
                            <Trash2 size={16} />
                        </button>
                    </div>
                );
            })}
             <div className="flex justify-between items-center mt-2">
                <button type="button" onClick={addAssignedResource} className="text-sm font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 flex items-center gap-1">
                    <Plus size={14}/> Add Resource
                </button>
            </div>
        </fieldset>

        <div className="flex gap-4"><input type="date" name="startDate" value={formData.startDate || ''} onChange={handleChange} className={commonInputClass} required /><input type="date" name="endDate" value={formData.endDate || ''} onChange={handleChange} className={commonInputClass} required /></div>
        <select name="reminder" value={formData.reminder || 'none'} onChange={handleChange} className={commonInputClass}>
            <option value="none">No Reminder</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="specific">Specific Date</option>
        </select>
        {formData.reminder === 'specific' && (
            <input 
                type="datetime-local" 
                name="specificReminderDateTime" 
                value={formData.specificReminderDateTime || ''} 
                onChange={handleChange} 
                className={commonInputClass} 
            />
        )}
      </>}
      <div className="mt-6 flex justify-end gap-4">
        <button type="button" onClick={onCancel} className="px-4 py-2 rounded bg-gray-200 text-gray-800 hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500">Cancel</button>
        <button type="submit" className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700">Save</button>
      </div>
    </form>
  );
};

const CalendarView = ({ tasks, resources, onUpdateTaskDates, onOpenModal }) => {
    const [view, setView] = useState('monthly'); // daily, weekly, monthly, yearly
    const [currentDate, setCurrentDate] = useState(new Date());

    const getResourceRole = (resourceId) => resources.find(r => r.id === resourceId)?.role || 'Default';
    
    const formatToYMD = (d) => {
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    const handleDrop = (e, date) => {
        e.preventDefault();
        const taskId = e.dataTransfer.getData("taskId");
        const taskDuration = parseInt(e.dataTransfer.getData("taskDuration"));
        
        const newStartDate = new Date(date);
        const newEndDate = new Date(newStartDate);
        newEndDate.setDate(newEndDate.getDate() + taskDuration);

        onUpdateTaskDates(taskId, formatToYMD(newStartDate), formatToYMD(newEndDate));
    };
    
    const handleDragStart = (e, task) => {
        e.dataTransfer.setData("taskId", task.id);
        const duration = dayDiff(new Date(task.startDate), new Date(task.endDate));
        e.dataTransfer.setData("taskDuration", duration);
    };

    const handleDateClick = (date) => {
        const formattedDate = formatToYMD(date);
        onOpenModal('Task', { startDate: formattedDate, endDate: formattedDate, reminder: 'none' });
    };
    
    const handleTaskClick = (e, task) => {
        e.stopPropagation();
        onOpenModal('Task', task);
    };

    const navigate = (amount) => {
        const newDate = new Date(currentDate);
        if (view === 'monthly') newDate.setMonth(newDate.getMonth() + amount);
        else if (view === 'weekly') newDate.setDate(newDate.getDate() + amount * 7);
        else if (view === 'daily') newDate.setDate(newDate.getDate() + amount);
        else if (view === 'yearly') newDate.setFullYear(newDate.getFullYear() + amount);
        setCurrentDate(newDate);
    };
    
    const renderHeader = () => {
        let title = '';
        if (view === 'monthly') title = currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });
        else if (view === 'weekly') {
            const startOfWeek = new Date(currentDate);
            startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
            const endOfWeek = new Date(startOfWeek);
            endOfWeek.setDate(endOfWeek.getDate() + 6);
            title = `${startOfWeek.toLocaleDateString()} - ${endOfWeek.toLocaleDateString()}`;
        }
        else if (view === 'daily') title = currentDate.toLocaleDateString('default', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
        else if (view === 'yearly') title = currentDate.getFullYear();

        return (
            <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-4">
                <div className="flex items-center gap-2">
                    <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"><ChevronLeft/></button>
                    <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 w-48 sm:w-64 text-center">{title}</h2>
                    <button onClick={() => navigate(1)} className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"><ChevronRight/></button>
                </div>
                <div className="flex items-center gap-2">
                    {['daily', 'weekly', 'monthly', 'yearly'].map(v => (
                        <button key={v} onClick={() => setView(v)} className={`px-3 py-1 rounded-md text-sm font-medium ${view === v ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-gray-600'}`}>{v.charAt(0).toUpperCase() + v.slice(1)}</button>
                    ))}
                </div>
            </div>
        );
    };

    const renderMonthlyView = () => {
        const month = currentDate.getMonth();
        const year = currentDate.getFullYear();
        const firstDayOfMonth = new Date(year, month, 1).getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const calendarDays = [];

        for (let i = 0; i < firstDayOfMonth; i++) { calendarDays.push(<div key={`empty-start-${i}`} className="border-r border-b dark:border-gray-700"></div>); }

        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(year, month, day);
            const dayTasks = tasks.filter(task => {
                const start = new Date(task.startDate); start.setHours(0,0,0,0);
                return date.getTime() === start.getTime();
            });

            calendarDays.push(
                <div key={day} className="border-r border-b dark:border-gray-700 p-1.5 min-h-[100px] relative" 
                     onClick={() => handleDateClick(date)}
                     onDragOver={(e) => e.preventDefault()}
                     onDrop={(e) => handleDrop(e, date)}>
                    <span className="text-sm font-medium">{day}</span>
                    <div className="mt-1 space-y-1">
                        {dayTasks.map(task => {
                            const priorityColor = PRIORITY_STYLES[task.priority] || PRIORITY_STYLES.Medium;
                            const statusStyle = STATUS_STYLES[task.status || 'TODO'];
                            const tooltip = `${task.name}\nStatus: ${task.status || 'TODO'}\nPriority: ${task.priority}\nStart: ${task.startDate}\nEnd: ${task.endDate}`;
                            return (
                                <div key={task.id} draggable onDragStart={(e) => handleDragStart(e, task)}
                                     onClick={(e) => handleTaskClick(e, task)}
                                     className={`${priorityColor} ${statusStyle.timeline} text-xs rounded px-1 py-0.5 truncate cursor-move`} title={tooltip}>
                                    {task.name}
                                </div>
                            );
                        })}
                    </div>
                </div>
            );
        }
        return (
            <div className="grid grid-cols-7">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => <div key={day} className="text-center font-bold text-sm text-gray-600 dark:text-gray-300 py-2 border-b-2 dark:border-gray-700">{day}</div>)}
                {calendarDays}
            </div>
        );
    };
    
    const renderWeeklyView = () => {
        const startOfWeek = new Date(currentDate);
        startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
        const weekDays = [];

        for (let i = 0; i < 7; i++) {
            const date = new Date(startOfWeek);
            date.setDate(date.getDate() + i);
            const dayTasks = tasks.filter(task => {
                const start = new Date(task.startDate); start.setHours(0,0,0,0);
                const end = new Date(task.endDate); end.setHours(0,0,0,0);
                return date >= start && date <= end;
            });
            weekDays.push(
                <div key={i} className="border-r border-b dark:border-gray-700 p-1.5 min-h-[200px]"
                     onClick={() => handleDateClick(date)}
                     onDragOver={(e) => e.preventDefault()}
                     onDrop={(e) => handleDrop(e, date)}>
                    <p className="font-bold text-center">{date.toLocaleDateString('default', { weekday: 'short' })}</p>
                    <p className="text-sm text-center mb-2">{date.getDate()}</p>
                    {dayTasks.map(task => {
                         const priorityColor = PRIORITY_STYLES[task.priority] || PRIORITY_STYLES.Medium;
                         const statusStyle = STATUS_STYLES[task.status || 'TODO'];
                         const tooltip = `${task.name}\nStatus: ${task.status || 'TODO'}\nPriority: ${task.priority}\nStart: ${task.startDate}\nEnd: ${task.endDate}`;
                         return (
                            <div key={task.id} draggable onDragStart={(e) => handleDragStart(e, task)}
                                 onClick={(e) => handleTaskClick(e, task)}
                                 className={`${priorityColor} ${statusStyle.timeline} text-xs rounded px-1 py-0.5 truncate cursor-move mb-1`} title={tooltip}>
                                {task.name}
                            </div>
                        );
                    })}
                </div>
            );
        }
        return <div className="grid grid-cols-7">{weekDays}</div>;
    };

    const renderDailyView = () => {
        const dayTasks = tasks.filter(task => {
            const start = new Date(task.startDate); start.setHours(0,0,0,0);
            const end = new Date(task.endDate); end.setHours(0,0,0,0);
            const current = new Date(currentDate); current.setHours(0,0,0,0);
            return current >= start && current <= end;
        });
        return (
            <div className="p-4">
                <button onClick={() => handleDateClick(currentDate)} className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm mb-4"><Plus size={16} /> Add Task for this Day</button>
                <div className="space-y-2">
                    {dayTasks.length > 0 ? dayTasks.map(task => {
                        const resourceNames = (task.assignedResources || []).map(ar => {
                            const resource = resources.find(r => r.id === ar.resourceId);
                            return resource ? `${resource.name} (${ar.weight}%)` : 'Unknown';
                        }).join(', ');
                        return (
                            <div key={task.id} className={`bg-gray-100 dark:bg-gray-700 p-3 rounded-md`}>
                                <p className="font-bold">{task.name}</p>
                                <p className="text-sm">Resource: {resourceNames}</p>
                            </div>
                        )
                    }) : <p>No tasks scheduled for this day.</p>}
                </div>
            </div>
        );
    };

    const renderYearlyView = () => {
        const year = currentDate.getFullYear();
        const months = [];
        for (let month = 0; month < 12; month++) {
            const monthDate = new Date(year, month, 1);
            const daysInMonth = new Date(year, month + 1, 0).getDate();
            const firstDayOfMonth = monthDate.getDay();
            const monthDays = [];
            for (let i = 0; i < firstDayOfMonth; i++) { monthDays.push(<div key={`empty-${month}-${i}`} className="w-4 h-4"></div>); }
            for (let day = 1; day <= daysInMonth; day++) {
                const date = new Date(year, month, day);
                const hasTasks = tasks.some(t => {
                    const start = new Date(t.startDate); start.setHours(0,0,0,0);
                    const end = new Date(t.endDate); end.setHours(0,0,0,0);
                    return date >= start && date <= end;
                });
                monthDays.push(<div key={day} className={`w-4 h-4 rounded-sm ${hasTasks ? 'bg-blue-400' : 'bg-gray-200 dark:bg-gray-600'}`}></div>);
            }
            months.push(
                <div key={month} className="p-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md" onClick={() => { setCurrentDate(monthDate); setView('monthly'); }}>
                    <p className="font-bold text-center mb-2">{monthDate.toLocaleString('default', { month: 'short' })}</p>
                    <div className="grid grid-cols-7 gap-1">{monthDays}</div>
                </div>
            );
        }
        return <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">{months}</div>;
    };

    return (
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
            {renderHeader()}
            {view === 'monthly' && renderMonthlyView()}
            {view === 'weekly' && renderWeeklyView()}
            {view === 'daily' && renderDailyView()}
            {view === 'yearly' && renderYearlyView()}
        </div>
    );
};

const ProjectTimeline = ({ projects, tasks, resources, timelineRange, onEdit, onDelete, sortBy, setSortBy }) => {
  const { timelineStart, totalDays } = timelineRange;
  const timelineBodyRef = useRef(null);
  const timelineHeaderRef = useRef(null);

  const handleScroll = () => {
      if (timelineHeaderRef.current && timelineBodyRef.current) {
          timelineHeaderRef.current.scrollLeft = timelineBodyRef.current.scrollLeft;
      }
  };

  const getBarStyle = (startDate, endDate, level = 0) => {
    const sDate = new Date(startDate);
    const eDate = new Date(endDate);
    const tStart = new Date(timelineStart);
    const left = dayDiff(tStart, sDate);
    const width = dayDiff(sDate, eDate) + 1;
    return {
      left: `${(left / totalDays) * 100}%`,
      width: `${(width / totalDays) * 100}%`,
      top: `${level * 2 + 0.5}rem`, // Stagger tasks, 2rem per level
      height: '1.5rem',
    };
  };

  const getResourceRole = (resourceId) => resources.find(r => r.id === resourceId)?.role || 'Default';
  
  const monthHeaders = useMemo(() => {
      if (!totalDays || totalDays <= 0) return [];
      const headers = [];
      let currentDate = new Date(timelineStart);
      const endDate = new Date(timelineStart);
      endDate.setDate(endDate.getDate() + totalDays);

      while(currentDate < endDate) {
          const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
          const endOfCurrentMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
          
          let effectiveStart = currentDate > startOfMonth ? currentDate : startOfMonth;
          let effectiveEnd = endOfCurrentMonth < endDate ? endOfCurrentMonth : endDate;

          const daysInMonth = dayDiff(effectiveStart, effectiveEnd) + 1;
          
          headers.push({
              name: startOfMonth.toLocaleString('default', { month: 'long', year: 'numeric' }),
              width: (daysInMonth / totalDays) * 100
          });
          currentDate.setMonth(currentDate.getMonth() + 1);
          currentDate.setDate(1);
      }
      return headers;
  }, [timelineStart, totalDays]);

  const sortedProjects = useMemo(() => {
    const projectsCopy = [...projects];
    if (sortBy === 'priority') {
        return projectsCopy.sort((a, b) => (PRIORITY_ORDER[b.priority] || 0) - (PRIORITY_ORDER[a.priority] || 0));
    }
    if (sortBy === 'duration') {
        return projectsCopy.sort((a, b) => dayDiff(b.startDate, b.endDate) - dayDiff(a.startDate, a.endDate));
    }
    return projectsCopy; // Default sort
  }, [projects, sortBy]);

  const getProjectedEndDate = (projectId) => {
    const projectTasks = tasks.filter(t => t.projectId === projectId);
    if (projectTasks.length === 0) return null;
    const maxEndDate = new Date(Math.max(...projectTasks.map(t => new Date(t.endDate))));
    return maxEndDate.toISOString().split('T')[0];
  };
  
  const timelineGridWidth = totalDays * 20; // 20px per day

  const projectsWithLayout = useMemo(() => {
    return sortedProjects.map(project => {
        const projectTasks = tasks.filter(t => t.projectId === project.id);
        const taskLevels = [];
        let maxLevel = 0;
        
        const sortedTasks = [...projectTasks].sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
        
        sortedTasks.forEach(task => {
            let level = 0;
            // Find the first level where this task can fit.
            // A task fits if its start date is after the next available start date for that level.
            while (taskLevels[level] && new Date(task.startDate) <= taskLevels[level]) {
                level++;
            }
            task.level = level;
            if (level > maxLevel) {
                maxLevel = level;
            }
            // Update the next available start date for this level.
            // It's the day after the current task ends.
            const taskEndDate = new Date(task.endDate);
            taskEndDate.setDate(taskEndDate.getDate() + 1); 
            taskLevels[level] = taskEndDate;
        });

        const rowHeight = Math.max(6, (maxLevel + 1) * 2.5 + 1); // min height of 6rem, 2.5rem per task level + padding
        return { ...project, tasks: sortedTasks, rowHeight: `${rowHeight}rem` };
    });
  }, [sortedProjects, tasks]);

  return (
    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center">
            <Calendar className="w-6 h-6 mr-3 text-blue-500" />
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Project Timeline</h2>
        </div>
        <div>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-1.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white">
                <option value="default">Sort by...</option>
                <option value="priority">Priority</option>
                <option value="duration">Duration</option>
            </select>
        </div>
      </div>
      <div className="grid grid-cols-[30%_70%]">
        {/* Header Left */}
        <div className="font-semibold text-gray-600 dark:text-gray-300 pr-2 flex items-end pb-2 border-b-2 border-gray-200 dark:border-gray-700">Project</div>
        
        {/* Header Right */}
        <div className="overflow-x-hidden" ref={timelineHeaderRef}>
             <div className="flex border-b-2 border-gray-200 dark:border-gray-700" style={{ width: `${timelineGridWidth}px`, height: '2.5rem'}}>
                {monthHeaders.map((month, i) => (
                    <div key={i} style={{flexBasis: `${month.width}%`}} className="text-center text-sm text-gray-500 dark:text-gray-400 border-l dark:border-gray-600 flex items-end pb-2 justify-center">
                        <span>{month.name}</span>
                    </div>
                ))}
            </div>
        </div>

        {/* Body Left */}
        <div className="pr-2 pt-4 max-h-[36rem] overflow-y-auto">
            {projectsWithLayout.map(project => {
                const projectedEndDate = getProjectedEndDate(project.id);
                const isDelayed = projectedEndDate && dayDiff(project.endDate, projectedEndDate) > 0;
                const projectTotalHours = tasks
                  .filter(t => t.projectId === project.id)
                  .reduce((projectSum, task) => {
                    const taskEffort = (task.assignedResources || []).reduce((taskSum, ar) => {
                      return taskSum + ((task.hours || 0) * (Number(ar.weight) || 0) / 100);
                    }, 0);
                    // If no resources, fall back to base task hours as the effort
                    return projectSum + (taskEffort > 0 ? taskEffort : (task.hours || 0));
                  }, 0);

                return (
                 <div key={project.id} style={{ height: project.rowHeight }} className="flex items-start pt-1 border-b border-gray-200 dark:border-gray-700">
                     <div className="flex justify-between items-start gap-2 w-full">
                        <div className="flex-grow min-w-0">
                            <p className="font-semibold truncate text-gray-700 dark:text-gray-200" title={project.name}>{project.name}</p>
                            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                               <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${PRIORITY_STYLES[project.priority]}`}>{project.priority}</span>
                               <span className="font-bold">{project.tshirtSize}</span>
                               <span className="font-medium">({projectTotalHours.toFixed(0)} hrs)</span>
                            </div>
                            <div className="text-xs mt-1">
                                <p>Planned: {project.endDate}</p>
                                {projectedEndDate && <p className={isDelayed ? 'text-red-500 font-bold' : ''}>Projected: {projectedEndDate}</p>}
                            </div>
                        </div>
                        <div className="flex items-center flex-shrink-0">
                            <button onClick={() => onEdit('Project', project)} className="p-1 text-gray-500 hover:text-blue-600 dark:hover:text-blue-400"><Edit size={14} /></button>
                            <button onClick={() => onDelete('Project', project.id)} className="p-1 text-gray-500 hover:text-red-600 dark:hover:text-red-400"><Trash2 size={14} /></button>
                        </div>
                    </div>
                 </div>
            )})}
        </div>

        {/* Body Right */}
        <div className="overflow-x-auto pt-4 max-h-[36rem] overflow-y-auto" ref={timelineBodyRef} onScroll={handleScroll}>
            <div className="relative" style={{ width: `${timelineGridWidth}px`}}>
                {projectsWithLayout.map(project => (
                    <div key={project.id} style={{ height: project.rowHeight }} className="border-b border-gray-200 dark:border-gray-700 relative">
                        <div className="absolute inset-0 bg-gray-100 dark:bg-gray-700 rounded-lg" style={{ height: `calc(${project.rowHeight} - 0.5rem)`, top: '0.25rem' }}></div>
                        {project.tasks.map(task => {
                          const priorityColor = PRIORITY_STYLES[task.priority] || PRIORITY_STYLES.Medium;
                          const statusStyle = STATUS_STYLES[task.status || 'TODO'];
                          const tooltip = `${task.name}\nStart: ${task.startDate}\nEnd: ${task.endDate}`;
                          return (
                            <div key={task.id} className={`absolute rounded-md opacity-90 flex items-center justify-center text-white text-xs px-2 overflow-hidden cursor-pointer ${priorityColor} ${statusStyle.timeline}`} 
                                 style={getBarStyle(task.startDate, task.endDate, task.level)} 
                                 title={tooltip}
                                 onClick={() => onEdit('Task', task)}>
                              <span className="whitespace-nowrap overflow-hidden">{task.name}</span>
                            </div>
                          );
                        })}
                    </div>
                ))}
            </div>
        </div>
      </div>
    </div>
  );
};

const ResourceManagement = ({ resources, tasks, projects, onEdit, onDelete, holidays }) => {
    const [expandedResourceId, setExpandedResourceId] = useState(null);

    const resourceAnalysis = useMemo(() => {
        const analysis = {};
        resources.forEach(resource => {
            const resourceTasks = tasks.filter(t => t.assignedResources && t.assignedResources.some(ar => ar.resourceId === resource.id));
            const conflicts = [];
            
            const dailyHoursMap = {};
            resourceTasks.forEach(task => {
                const assignment = task.assignedResources.find(ar => ar.resourceId === resource.id);
                if (!assignment) return;

                const weight = Number(assignment.weight) || 0;
                const hoursPerResource = (task.hours || 0) * (weight / 100);
                
                const workingDays = getWorkingDays(task.startDate, task.endDate, resource.leave, holidays);
                const dailyHours = workingDays > 0 ? hoursPerResource / workingDays : 0;
                
                let d = new Date(task.startDate);
                while (d <= new Date(task.endDate)) {
                    const dateStr = d.toISOString().split('T')[0];
                    if(getWorkingDays(d,d,resource.leave, holidays) === 1) {
                        dailyHoursMap[dateStr] = (dailyHoursMap[dateStr] || 0) + dailyHours;
                    }
                    d.setDate(d.getDate() + 1);
                }
            });

            for (const dateStr in dailyHoursMap) {
                if (dailyHoursMap[dateStr] > DAILY_WORK_HOURS) {
                    const conflictingTasks = resourceTasks.filter(task => {
                        const start = new Date(task.startDate);
                        const end = new Date(task.endDate);
                        const current = new Date(dateStr);
                        return current >= start && current <= end;
                    });
                    if (conflictingTasks.length > 1) {
                         conflicts.push({ 
                            tasks: conflictingTasks.map(t => t.name), 
                            date: dateStr,
                            excessHours: dailyHoursMap[dateStr] - DAILY_WORK_HOURS
                        });
                    }
                }
            }
            
            const totalAssignedHours = resourceTasks.reduce((sum, task) => {
                const assignment = task.assignedResources.find(ar => ar.resourceId === resource.id);
                const weight = assignment ? (Number(assignment.weight) || 0) : 0;
                return sum + ((task.hours || 0) * (weight / 100));
            }, 0);
            
            let totalAvailableHours = 0;
            if (resourceTasks.length > 0) {
                const minDate = new Date(Math.min(...resourceTasks.map(t => new Date(t.startDate))));
                const maxDate = new Date(Math.max(...resourceTasks.map(t => new Date(t.endDate))));
                totalAvailableHours = getWorkingDays(minDate, maxDate, resource.leave, holidays) * DAILY_WORK_HOURS;
            }
            const totalLoad = totalAvailableHours > 0 ? (totalAssignedHours / totalAvailableHours) * 100 : 0;

            analysis[resource.id] = { conflicts, totalLoad };
        });
        return analysis;
    }, [resources, tasks, holidays]);
    
    const getResourceProjects = (resourceId) => {
        return projects.filter(p => tasks.some(t => t.projectId === p.id && t.assignedResources && t.assignedResources.some(ar => ar.resourceId === resourceId))).map(p => p.name).join(', ');
    };

    return (
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
            <div className="flex items-center mb-4"><Users className="w-6 h-6 mr-3 text-green-500" /><h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Resource Management</h2></div>
            <div className="space-y-4">
                {resources.map(resource => {
                    const role = resource.role || 'Default';
                    const roleInfo = ROLES[role];
                    const analysis = resourceAnalysis[resource.id];
                    const hasConflict = analysis.conflicts.length > 0;
                    const assignedProjects = getResourceProjects(resource.id);
                    const loadColor = analysis.totalLoad > 100 ? 'bg-red-500' : analysis.totalLoad > 80 ? 'bg-yellow-500' : 'bg-green-500';

                    return (
                        <div key={resource.id} className={`p-4 rounded-lg border ${hasConflict ? 'border-red-400 dark:border-red-600 bg-red-50 dark:bg-red-900/40' : 'border-transparent ' + roleInfo.lightBgColor}`}>
                            <div className="flex justify-between items-start">
                                <div className="flex-grow min-w-0">
                                    <p className="font-semibold text-gray-800 dark:text-gray-100">{resource.name}</p>
                                    <p className={`text-sm font-medium text-${roleInfo.color}-600 dark:text-${roleInfo.color}-400`}>{resource.role}</p>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                    {hasConflict && <div className="flex items-center text-red-500 dark:text-red-400 font-bold"><AlertTriangle className="w-5 h-5 mr-2" /><span>Task Conflict</span></div>}
                                    <div className="flex items-center">
                                        <button onClick={() => onEdit('Resource', resource)} className="p-1 text-gray-500 hover:text-blue-600 dark:hover:text-blue-400"><Edit size={14} /></button>
                                        <button onClick={() => onDelete('Resource', resource.id)} className="p-1 text-gray-500 hover:text-red-600 dark:hover:text-red-400"><Trash2 size={14} /></button>
                                    </div>
                                </div>
                            </div>
                            <div className="mt-2 text-sm text-gray-600 dark:text-gray-400 space-y-2">
                                <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
                                    <div className={`${loadColor} h-2.5 rounded-full`} style={{width: `${Math.min(analysis.totalLoad, 100)}%`}}></div>
                                </div>
                                <p><strong>Total Load:</strong> {analysis.totalLoad.toFixed(1)}%</p>
                                {assignedProjects && <p><strong>Projects:</strong> {assignedProjects}</p>}
                                {resource.leave?.filter(l => l.startDate).length > 0 && (
                                    <div>
                                        <p><strong>On Leave:</strong></p>
                                        <ul className="list-disc list-inside ml-4 font-medium text-yellow-600 dark:text-yellow-400">
                                            {resource.leave.map((l, i) => (
                                                l.startDate && <li key={i}>{l.startDate} to {l.endDate}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {hasConflict && (
                                    <div className="text-red-700 dark:text-red-300 p-2 bg-red-100 dark:bg-red-900/50 rounded-md">
                                        <p className="font-bold">Conflict Details:</p>
                                        <ul className="list-disc list-inside ml-4">
                                        {analysis.conflicts.map((c, i) => 
                                            <li key={i}>
                                                Overallocated on {c.date} by {c.excessHours.toFixed(1)} hrs. Tasks: {c.tasks.join(', ')}
                                            </li>
                                        )}
                                        </ul>
                                    </div>
                                )}
                                <button
                                    onClick={() => setExpandedResourceId(expandedResourceId === resource.id ? null : resource.id)}
                                    className="text-xs font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 mt-2 flex items-center gap-1"
                                >
                                    {expandedResourceId === resource.id ? 'Hide Tasks' : 'Show Tasks'}
                                    <ChevronDown className={`w-4 h-4 transition-transform ${expandedResourceId === resource.id ? 'rotate-180' : ''}`} />
                                </button>
                                {expandedResourceId === resource.id && (
                                    <div className="mt-2 pt-2 border-t border-gray-300 dark:border-gray-600">
                                        <div className="space-y-2 max-h-40 overflow-y-auto pr-2">
                                            {tasks
                                                .filter(t => t.assignedResources && t.assignedResources.some(ar => ar.resourceId === resource.id))
                                                .map(task => {
                                                    const project = projects.find(p => p.id === task.projectId);
                                                    const assignment = task.assignedResources.find(ar => ar.resourceId === resource.id);
                                                    return (
                                                        <div key={task.id} className="text-xs p-2 bg-gray-200/50 dark:bg-gray-900/50 rounded-md">
                                                            <p className="font-bold text-gray-800 dark:text-gray-100">{task.name} ({assignment.weight}%)</p>
                                                            <p className="text-gray-500 dark:text-gray-400">Project: {project ? project.name : 'N/A'}</p>
                                                        </div>
                                                    );
                                                })
                                            }
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const ProjectResourceAssignment = ({ projects, resources, onUpdateAssignments }) => {
    const handleAssignmentChange = (projectId, assignedResourceIds) => {
        onUpdateAssignments(projectId, assignedResourceIds);
    };

    return (
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
            <div className="flex items-center mb-4"><LinkIcon className="w-6 h-6 mr-3 text-cyan-500" /><h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Project Resource Assignment</h2></div>
            <div className="space-y-4">
                {projects.map(project => (
                    <div key={project.id} className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                        <h3 className="font-bold text-gray-800 dark:text-gray-100 mb-2">{project.name}</h3>
                        <select 
                            multiple
                            value={(project.assignedResources || []).map(String)}
                            onChange={(e) => handleAssignmentChange(project.id, Array.from(e.target.selectedOptions, option => option.value))}
                            className="w-full p-2 border rounded bg-white dark:bg-gray-800 dark:border-gray-600 h-32"
                        >
                            {resources.map(resource => (
                                <option key={resource.id} value={resource.id}>
                                    {resource.name} ({resource.role})
                                </option>
                            ))}
                        </select>
                    </div>
                ))}
            </div>
        </div>
    );
};

const TaskList = ({ tasks, projects, resources, onEdit, onDelete, sortBy, setSortBy, statusFilter, setStatusFilter, projectFilter, setProjectFilter, resourceFilter, setResourceFilter }) => {
    const getProjectName = (projectId) => projects.find(p => p.id === projectId)?.name || 'N/A';
    const getResourceNames = (assignedResources) => {
        if (!assignedResources || assignedResources.length === 0) return 'Unassigned';
        return assignedResources.map(ar => {
            const resource = resources.find(r => r.id === ar.resourceId);
            return resource ? `${resource.name} (${ar.weight}%)` : 'Unknown';
        }).join(', ');
    };

    const filteredAndSortedTasks = useMemo(() => {
        let filteredTasks = [...tasks];

        if (projectFilter !== 'All') {
            filteredTasks = filteredTasks.filter(task => task.projectId === projectFilter);
        }
        
        if (resourceFilter !== 'All') {
            filteredTasks = filteredTasks.filter(task => 
                task.assignedResources && task.assignedResources.some(ar => ar.resourceId === resourceFilter)
            );
        }

        if (statusFilter !== 'All') {
            filteredTasks = filteredTasks.filter(task => (task.status || 'TODO') === statusFilter);
        }

        if (sortBy === 'priority') {
            return filteredTasks.sort((a, b) => (PRIORITY_ORDER[b.priority] || 0) - (PRIORITY_ORDER[a.priority] || 0));
        }
        if (sortBy === 'duration') {
            return filteredTasks.sort((a, b) => (a.hours || 0) - (b.hours || 0));
        }
        if (sortBy === 'project') {
            const projectNames = projects.reduce((acc, p) => ({ ...acc, [p.id]: p.name }), {});
            return filteredTasks.sort((a, b) => {
                const nameA = projectNames[a.projectId] || '';
                const nameB = projectNames[b.projectId] || '';
                return nameA.localeCompare(nameB);
            });
        }
        if (sortBy === 'resource') {
             const resourceNames = resources.reduce((acc, r) => ({ ...acc, [r.id]: r.name }), {});
             return filteredTasks.sort((a, b) => {
                const firstResourceA = a.assignedResources?.[0]?.resourceId;
                const firstResourceB = b.assignedResources?.[0]?.resourceId;
                const nameA = firstResourceA ? resourceNames[firstResourceA] : 'zzzz'; // 'zzzz' to put unassigned last
                const nameB = firstResourceB ? resourceNames[firstResourceB] : 'zzzz';
                return nameA.localeCompare(nameB);
             });
        }
        // Default is startDate
        return filteredTasks.sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
    }, [tasks, sortBy, statusFilter, projectFilter, resourceFilter, projects, resources]);

    const commonSelectClass = "bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-1.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white";

    return (
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
            <div className="flex flex-col md:flex-row justify-between items-center mb-4 gap-4">
                <div className="flex items-center">
                    <Briefcase className="w-6 h-6 mr-3 text-purple-500" />
                    <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">All Tasks</h2>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 w-full md:w-auto">
                    <select value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)} className={commonSelectClass}>
                        <option value="All">All Projects</option>
                        {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                    <select value={resourceFilter} onChange={(e) => setResourceFilter(e.target.value)} className={commonSelectClass}>
                        <option value="All">All Resources</option>
                        {resources.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                    <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={commonSelectClass}>
                        <option value="All">All Statuses</option>
                        <option value="TODO">TODO</option>
                        <option value="In Progress">In Progress</option>
                        <option value="Complete">Complete</option>
                    </select>
                    <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className={commonSelectClass}>
                        <option value="startDate">Sort by Start Date</option>
                        <option value="priority">Priority</option>
                        <option value="duration">Duration</option>
                        <option value="project">Project</option>
                        <option value="resource">Resource</option>
                    </select>
                </div>
            </div>
            <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                {filteredAndSortedTasks.map(task => (
                    <div key={task.id} className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 flex justify-between items-center">
                        <div>
                            <p className="font-semibold text-gray-800 dark:text-gray-100">{task.name}</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                Project: {getProjectName(task.projectId)} | Resources: {getResourceNames(task.assignedResources)}
                            </p>
                            <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500">
                                <span>{task.startDate} to {task.endDate} ({task.hours} hrs)</span>
                                <span className={`px-1.5 py-0.5 rounded-full text-xs font-semibold ${PRIORITY_STYLES[task.priority]}`}>{task.priority}</span>
                                <span className={`px-1.5 py-0.5 rounded-full text-xs font-semibold ${STATUS_STYLES[task.status || 'TODO'].badge}`}>{task.status || 'TODO'}</span>
                            </div>
                        </div>
                        <div className="flex items-center flex-shrink-0">
                            <button onClick={() => onEdit('Task', task)} className="p-1 text-gray-500 hover:text-blue-600 dark:hover:text-blue-400"><Edit size={14} /></button>
                            <button onClick={() => onDelete('Task', task.id)} className="p-1 text-gray-500 hover:text-red-600 dark:hover:text-red-400"><Trash2 size={14} /></button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const ProjectStaffingAnalysis = ({ projects, resources, tasks, holidays }) => {
    const analysis = useMemo(() => {
        return projects.map(project => {
            const projectTasks = tasks.filter(t => t.projectId === project.id);
            const requiredHours = projectTasks.reduce((projectSum, task) => {
                const taskEffort = (task.assignedResources || []).reduce((taskSum, ar) => {
                    return taskSum + ((task.hours || 0) * (Number(ar.weight) || 0) / 100);
                }, 0);
                return projectSum + (taskEffort > 0 ? taskEffort : (task.hours || 0));
            }, 0);
            
            let availableHours = 0;
            const projectResources = project.assignedResources || [];

            projectResources.forEach(resourceId => {
                const resource = resources.find(r => r.id === resourceId);
                if (!resource) return;
                const workingDays = getWorkingDays(project.startDate, project.endDate, resource.leave, holidays);
                availableHours += workingDays * DAILY_WORK_HOURS;
            });

            const utilization = availableHours > 0 ? (requiredHours / availableHours) * 100 : 0;
            const needsHiring = requiredHours > availableHours;

            return { ...project, requiredHours, availableHours, utilization, needsHiring };
        });
    }, [projects, resources, tasks, holidays]);

    return (
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
            <div className="flex items-center mb-4"><UserCheck className="w-6 h-6 mr-3 text-indigo-500" /><h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Project Staffing Analysis</h2></div>
            <div className="space-y-4">
                {analysis.map(proj => (
                    <div key={proj.id} className={`p-4 rounded-lg ${proj.needsHiring ? 'bg-red-100 dark:bg-red-900/30' : 'bg-gray-50 dark:bg-gray-700/50'}`}>
                        <p className="font-bold text-gray-800 dark:text-gray-100">{proj.name}</p>
                        <div className="mt-2 text-sm grid grid-cols-2 gap-x-4 gap-y-1 text-gray-600 dark:text-gray-300">
                            <span>Required Hours:</span><span className="font-medium">{proj.requiredHours.toFixed(0)}</span>
                            <span>Available Hours:</span><span className="font-medium">{proj.availableHours.toFixed(0)}</span>
                            <span>Utilization:</span><span className="font-medium">{proj.utilization.toFixed(1)}%</span>
                        </div>
                        {proj.needsHiring && <div className="mt-3 text-center p-2 rounded-md bg-red-200 dark:bg-red-500/30 text-red-800 dark:text-red-200 font-bold text-sm">Hiring Recommended</div>}
                    </div>
                ))}
            </div>
        </div>
    );
};

const Reminders = ({ tasks, projects }) => {
    const upcomingReminders = useMemo(() => {
        const today = new Date(); // Use current date for real reminders
        today.setHours(0, 0, 0, 0);
        const nextWeek = new Date(today);
        nextWeek.setDate(today.getDate() + 7);

        const reminders = [];
        tasks.forEach(task => {
            if (task.reminder === 'none' || !task.startDate) return;

            if (task.reminder === 'specific' && task.specificReminderDateTime) {
                const reminderDate = new Date(task.specificReminderDateTime);
                if (reminderDate >= today && reminderDate <= nextWeek) {
                    reminders.push({
                        id: `${task.id}-${task.specificReminderDateTime}`,
                        taskName: task.name,
                        projectId: task.projectId,
                        dueDate: task.specificReminderDateTime.split('T')[0],
                    });
                }
            } else {
                let currentDate = new Date(task.startDate);
                const endDate = new Date(task.endDate);

                while(currentDate <= endDate) {
                    if (currentDate >= today && currentDate <= nextWeek) {
                        reminders.push({
                            id: `${task.id}-${currentDate.toISOString()}`,
                            taskId: task.id,
                            taskName: task.name,
                            projectId: task.projectId,
                            dueDate: currentDate.toISOString().split('T')[0],
                        });
                    }
                    
                    switch(task.reminder) {
                        case 'daily':
                            currentDate.setDate(currentDate.getDate() + 1);
                            break;
                        case 'weekly':
                            currentDate.setDate(currentDate.getDate() + 7);
                            break;
                        case 'monthly':
                            currentDate.setMonth(currentDate.getMonth() + 1);
                            break;
                        default:
                            currentDate = new Date(endDate);
                            currentDate.setDate(currentDate.getDate() + 1);
                            break;
                    }
                }
            }
        });

        return reminders.sort((a,b) => new Date(a.dueDate) - new Date(b.dueDate));
    }, [tasks]);

    const getProjectName = (projectId) => projects.find(p => p.id === projectId)?.name || 'N/A';
    const todayStr = new Date().toISOString().split('T')[0];

    return (
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
            <div className="flex items-center mb-4"><Bell className="w-6 h-6 mr-3 text-yellow-500" /><h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Upcoming Reminders</h2></div>
            <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                {upcomingReminders.length > 0 ? (
                    upcomingReminders.map(reminder => (
                        <div key={reminder.id} className={`p-3 rounded-lg ${reminder.dueDate === todayStr ? 'bg-yellow-100 dark:bg-yellow-900/30' : 'bg-gray-100 dark:bg-gray-700/50'}`}>
                            <p className={`font-semibold ${reminder.dueDate === todayStr ? 'text-yellow-800 dark:text-yellow-200' : 'text-gray-800 dark:text-gray-200'}`}>{reminder.taskName}</p>
                            <p className={`text-sm ${reminder.dueDate === todayStr ? 'text-yellow-700 dark:text-yellow-300' : 'text-gray-600 dark:text-gray-400'}`}>Project: {getProjectName(reminder.projectId)}</p>
                            <p className={`text-sm ${reminder.dueDate === todayStr ? 'text-yellow-700 dark:text-yellow-300' : 'text-gray-600 dark:text-gray-400'}`}>Due: {reminder.dueDate}</p>
                        </div>
                    ))
                ) : <p className="text-gray-500 dark:text-gray-400">No reminders in the next 7 days.</p>}
            </div>
        </div>
    );
};

const Notification = ({ notification, onDismiss }) => (
  <div style={{
      backgroundColor: '#3B82F6',
      color: 'white',
      padding: '1rem',
      borderRadius: '0.5rem',
      boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
      display: 'flex',
      alignItems: 'flex-start',
      gap: '1rem',
      animation: 'fade-in-right 0.5s ease-out forwards'
  }}>
    <Bell className="h-6 w-6 mt-1" />
    <div style={{flex: 1}}>
      <h4 style={{fontWeight: 'bold'}}>{notification.title}</h4>
      <p style={{fontSize: '0.875rem'}}>{notification.message}</p>
    </div>
    <button onClick={() => onDismiss(notification.id)} style={{padding: '0.25rem', borderRadius: '9999px'}}>
      <X className="h-5 w-5" />
    </button>
  </div>
);

const NotificationContainer = ({ notifications, onDismiss }) => (
  <div style={{
      position: 'fixed',
      top: '5rem',
      right: '1rem',
      zIndex: 50,
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem'
  }}>
    {notifications.map(n => (
      <Notification key={n.id} notification={n} onDismiss={onDismiss} />
    ))}
  </div>
);

const ProfileModal = ({ auth, onClose, onClearDashboard }) => {
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const [confirmClearText, setConfirmClearText] = useState('');

    const user = auth.currentUser;
    const isPasswordUser = user.providerData.some(p => p.providerId === 'password');

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        if (newPassword !== confirmPassword) {
            setError("New passwords don't match.");
            return;
        }
        if (newPassword.length < 6) {
            setError("Password should be at least 6 characters.");
            return;
        }

        setLoading(true);
        try {
            const credential = window.firebase.auth.EmailAuthProvider.credential(user.email, currentPassword);
            await user.reauthenticateWithCredential(credential);
            await user.updatePassword(newPassword);
            setSuccess('Password updated successfully!');
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };
    
    const handleClear = () => {
        if (confirmClearText === 'delete all') {
            onClearDashboard();
            onClose();
        } else {
            setError("Please type 'delete all' to confirm.");
        }
    }

    const commonInputClass = "w-full p-2 border rounded bg-gray-50 dark:bg-gray-700 dark:border-gray-600 mb-4";

    return (
        <Modal onClose={onClose}>
            <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-gray-100">User Profile & Settings</h2>
            <div className="mb-6">
                <p className="text-sm text-gray-500 dark:text-gray-400">Signed in as</p>
                <p className="font-medium text-gray-800 dark:text-gray-200">{user.email}</p>
            </div>
            
            {isPasswordUser && (
                <form onSubmit={handlePasswordChange} className="mb-8">
                    <h3 className="text-lg font-semibold mb-2 text-gray-700 dark:text-gray-200">Change Password</h3>
                    <input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} placeholder="Current Password" className={commonInputClass} required />
                    <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="New Password" className={commonInputClass} required />
                    <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Confirm New Password" className={commonInputClass} required />
                    {success && <p className="text-sm text-center text-green-500 mb-4">{success}</p>}
                    <div className="flex justify-end">
                        <button type="submit" disabled={loading} className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:bg-blue-300">
                            {loading ? 'Updating...' : 'Update Password'}
                        </button>
                    </div>
                </form>
            )}

            <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-red-500 dark:text-red-400">Danger Zone</h3>
                <div className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg mt-2">
                    <p className="text-sm text-red-800 dark:text-red-200 mb-2">
                        This will permanently delete all projects, resources, and tasks on the <strong>current dashboard</strong>. This action cannot be undone.
                    </p>
                    <p className="text-sm text-red-800 dark:text-red-200 mb-4">
                        To confirm, please type <strong>delete all</strong> into the box below.
                    </p>
                    <input type="text" value={confirmClearText} onChange={e => setConfirmClearText(e.target.value)} placeholder="delete all" className={commonInputClass} />
                    <button onClick={handleClear} disabled={confirmClearText !== 'delete all'} className="w-full px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700 disabled:bg-red-400 disabled:cursor-not-allowed">
                        Clear Current Dashboard
                    </button>
                </div>
            </div>
            {error && <p className="text-sm text-center text-red-500 mt-4">{error}</p>}
        </Modal>
    );
};

const HolidaysModal = ({ holidays, onSave, onClose }) => {
    const [newHoliday, setNewHoliday] = useState('');
    const [currentHolidays, setCurrentHolidays] = useState(holidays || []);

    const handleAddHoliday = () => {
        if (newHoliday && !currentHolidays.includes(newHoliday)) {
            setCurrentHolidays([...currentHolidays, newHoliday].sort());
            setNewHoliday('');
        }
    };

    const handleRemoveHoliday = (holidayToRemove) => {
        setCurrentHolidays(currentHolidays.filter(h => h !== holidayToRemove));
    };

    const handleSave = () => {
        onSave(currentHolidays);
        onClose();
    };
    
    const commonInputClass = "w-full p-2 border rounded bg-gray-50 dark:bg-gray-700 dark:border-gray-600 mb-4";

    return (
        <Modal onClose={onClose}>
            <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-gray-100">Annual Holidays</h2>
            <div className="space-y-4">
                <div className="flex gap-2">
                    <input type="date" value={newHoliday} onChange={e => setNewHoliday(e.target.value)} className={commonInputClass + " mb-0"} />
                    <button onClick={handleAddHoliday} className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700">Add</button>
                </div>
                <div className="max-h-64 overflow-y-auto space-y-2">
                    {currentHolidays.map(holiday => (
                        <div key={holiday} className="flex justify-between items-center bg-gray-100 dark:bg-gray-700 p-2 rounded-md">
                            <span>{holiday}</span>
                            <button onClick={() => handleRemoveHoliday(holiday)} className="p-1 text-red-500 hover:text-red-700 dark:hover:text-red-300 rounded-full hover:bg-red-100 dark:hover:bg-red-900/50">
                                <Trash2 size={16} />
                            </button>
                        </div>
                    ))}
                </div>
            </div>
             <div className="mt-6 flex justify-end gap-4">
                    <button type="button" onClick={onClose} className="px-4 py-2 rounded bg-gray-200 text-gray-800 hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500">Cancel</button>
                    <button type="button" onClick={handleSave} className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700">Save Holidays</button>
                </div>
        </Modal>
    );
};

const ImportExportModal = ({ onClose, onExportJson, onExportExcel, onExportCsv, onImport, xlsxReady }) => {
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const [fileContent, setFileContent] = useState(null);
  const [fileType, setFileType] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    const reader = new FileReader();

    if (selectedFile.type === 'application/json') {
      setFileType('json');
      reader.onload = (ev) => {
        try {
          const content = JSON.parse(ev.target.result);
          setFileContent(content);
          setError('');
          setShowConfirm(true);
        } catch (err) {
          setError('Failed to parse JSON file.');
        }
      };
      reader.readAsText(selectedFile);
    } else if (selectedFile.name.endsWith('.csv') || selectedFile.name.endsWith('.xlsx')) {
        setFileType(selectedFile.name.endsWith('.csv') ? 'csv' : 'excel');
        reader.onload = (ev) => {
            try {
                if (!xlsxReady) {
                    setError("Excel/CSV library not ready. Please try again in a moment.");
                    return;
                }
                const data = ev.target.result;
                const workbook = window.XLSX.read(data, { type: 'binary' });
                const content = {};
                workbook.SheetNames.forEach(sheetName => {
                    content[sheetName] = window.XLSX.utils.sheet_to_json(workbook.Sheets[sheetName]);
                });
                setFileContent(content);
                setError('');
                setShowConfirm(true);
            } catch (err) {
                console.error(err);
                setError('Failed to parse file.');
            }
        };
        reader.readAsBinaryString(selectedFile);
    } else {
      setError('Please select a valid .json, .csv, or .xlsx file.');
    }
  };

  const handleConfirmImport = () => {
    if (fileContent) {
      onImport(fileContent, fileType);
    }
  };

  return (
    <Modal onClose={onClose}>
      <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-gray-100">Import / Export Data</h2>
      
      {!showConfirm ? (
        <>
          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-2 text-gray-700 dark:text-gray-200">Export Data for Current Dashboard</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Use JSON for a complete backup. Use Excel or CSV to work with data in other applications.</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <button onClick={onExportJson} className="flex justify-center items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                    <Download size={18} /> JSON
                </button>
                <button onClick={onExportExcel} disabled={!xlsxReady} className="flex justify-center items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed">
                    <Download size={18} /> Excel
                </button>
                <button onClick={onExportCsv} disabled={!xlsxReady} className="flex justify-center items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed">
                    <Download size={18} /> CSV
                </button>
            </div>
             {!xlsxReady && <p className="text-xs text-center mt-2 text-gray-500 dark:text-gray-400">Excel/CSV export is initializing...</p>}
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-2 text-gray-700 dark:text-gray-200">Import Data to Current Dashboard</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Select a file to import. This will <span className="font-bold text-red-500">overwrite all existing data on this dashboard.</span></p>
            <div className="relative">
                <label htmlFor="file-upload" className="w-full flex justify-center items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors cursor-pointer">
                    <Upload size={18} /> Select File (.json, .xlsx, .csv)
                </label>
                <input id="file-upload" type="file" accept=".json,.csv,.xlsx" onChange={handleFileChange} className="hidden" />
            </div>
            {error && <p className="text-sm text-red-500 mt-2 text-center">{error}</p>}
          </div>
        </>
      ) : (
        <>
          <h3 className="text-lg font-semibold mb-2 text-gray-700 dark:text-gray-200">Confirm Import</h3>
          <div className="p-4 bg-yellow-100 dark:bg-yellow-900/30 border-l-4 border-yellow-500 text-yellow-800 dark:text-yellow-200 mb-4 rounded-r-md">
            <p className="font-bold">Warning!</p>
            <p className="text-sm">Importing from <strong>{file.name}</strong> will <span className="font-bold">overwrite and replace</span> all data on the current dashboard. This action cannot be undone.</p>
          </div>
          <div className="mt-6 flex justify-end gap-4">
            <button type="button" onClick={() => setShowConfirm(false)} className="px-4 py-2 rounded bg-gray-200 text-gray-800 hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500">Cancel</button>
            <button type="button" onClick={handleConfirmImport} className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700">Yes, Overwrite and Import</button>
          </div>
        </>
      )}
    </Modal>
  );
};

const DashboardSwitcher = ({ dashboards, activeDashboardId, onSwitch, onCreate, onDelete, onRename, onCopy }) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isSubMenuOpen, setIsSubMenuOpen] = useState(null);
    const activeDashboard = dashboards.find(d => d.id === activeDashboardId);
    const menuRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setIsMenuOpen(false);
                setIsSubMenuOpen(null);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <div className="relative" ref={menuRef}>
            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600">
                <LayoutDashboard size={18} />
                <span className="font-semibold text-gray-800 dark:text-gray-100 truncate max-w-[150px]">{activeDashboard ? activeDashboard.name : 'No Dashboard'}</span>
                <ChevronDown size={16} className={`transition-transform ${isMenuOpen ? 'rotate-180' : ''}`} />
            </button>
            {isMenuOpen && (
                <div className="absolute top-full mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border dark:border-gray-700 z-50">
                    <div className="p-2">
                        {dashboards.map(dashboard => (
                            <div key={dashboard.id} className={`w-full text-left px-3 py-2 rounded-md text-sm flex justify-between items-center ${dashboard.id === activeDashboardId ? 'bg-blue-100 dark:bg-blue-900/50' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
                                <button onClick={() => { onSwitch(dashboard.id); setIsMenuOpen(false); }} className="truncate flex-grow text-left">
                                    {dashboard.name}
                                </button>
                                <div className="relative">
                                    <button onClick={() => setIsSubMenuOpen(isSubMenuOpen === dashboard.id ? null : dashboard.id)} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"><MoreVertical size={16}/></button>
                                    {isSubMenuOpen === dashboard.id && (
                                        <div className="absolute right-0 top-full mt-1 w-32 bg-white dark:bg-gray-700 rounded-md shadow-lg border dark:border-gray-600 z-10">
                                            <button onClick={() => { onRename(dashboard.id, dashboard.name); setIsMenuOpen(false); setIsSubMenuOpen(null); }} className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-gray-100 dark:hover:bg-gray-600"><Edit size={14}/> Rename</button>
                                            <button onClick={() => { onCopy(dashboard.id); setIsMenuOpen(false); setIsSubMenuOpen(null); }} className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-gray-100 dark:hover:bg-gray-600"><Copy size={14}/> Copy</button>
                                            {dashboards.length > 1 && <button onClick={() => { onDelete(dashboard.id, dashboard.name); setIsMenuOpen(false); setIsSubMenuOpen(null); }} className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/50"><Trash2 size={14}/> Delete</button>}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="border-t border-gray-200 dark:border-gray-700 p-2">
                        <button onClick={() => { onCreate(); setIsMenuOpen(false); }} className="w-full text-left px-3 py-2 rounded-md text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2">
                            <Plus size={16} /> Create New Dashboard
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

const DashboardModal = ({ onClose, onSave, initialName = '', title }) => {
    const [name, setName] = useState(initialName);
    return (
        <Modal onClose={onClose}>
            <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-gray-100">{title}</h2>
            <input 
                value={name} 
                onChange={e => setName(e.target.value)} 
                placeholder="Dashboard Name" 
                className="w-full p-2 border rounded bg-gray-50 dark:bg-gray-700 dark:border-gray-600 mb-4" 
            />
            <div className="mt-6 flex justify-end gap-4">
                <button type="button" onClick={onClose} className="px-4 py-2 rounded bg-gray-200 text-gray-800 hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500">Cancel</button>
                <button type="button" onClick={() => onSave(name)} disabled={!name} className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:bg-blue-400">Save</button>
            </div>
        </Modal>
    );
};

const ConfirmationModal = ({ isOpen, onClose, onConfirm, title, message }) => {
    if (!isOpen) return null;
    return (
        <Modal onClose={onClose}>
            <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-gray-100">{title}</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">{message}</p>
            <div className="flex justify-end gap-4">
                <button onClick={onClose} className="px-4 py-2 rounded bg-gray-200 text-gray-800 hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500">
                    Cancel
                </button>
                <button onClick={onConfirm} className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700">
                    Confirm Delete
                </button>
            </div>
        </Modal>
    );
};

// --- MAIN APP COMPONENT ---

export default function App() {
  const [user, setUser] = useState(null);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [projects, setProjects] = useState([]);
  const [resources, setResources] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [dashboards, setDashboards] = useState([]);
  const [activeDashboardId, setActiveDashboardId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalConfig, setModalConfig] = useState({ type: '', data: null });
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isHolidaysModalOpen, setIsHolidaysModalOpen] = useState(false);
  const [isImportExportModalOpen, setIsImportExportModalOpen] = useState(false);
  const [isDashboardModalOpen, setIsDashboardModalOpen] = useState(false);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [renameModalConfig, setRenameModalConfig] = useState({ id: null, name: '' });
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [confirmModalConfig, setConfirmModalConfig] = useState({ onConfirm: () => {}, title: '', message: '' });
  const [darkMode, setDarkMode] = useState(false);
  const [firebaseServices, setFirebaseServices] = useState({ auth: null, db: null });
  const [sdkReady, setSdkReady] = useState(false);
  const [xlsxReady, setXlsxReady] = useState(false);
  const [projectSortBy, setProjectSortBy] = useState('default');
  const [taskSortBy, setTaskSortBy] = useState('startDate');
  const [taskStatusFilter, setTaskStatusFilter] = useState('All');
  const [taskProjectFilter, setTaskProjectFilter] = useState('All');
  const [taskResourceFilter, setTaskResourceFilter] = useState('All');
  const [activeTab, setActiveTab] = useState('home');
  const [notifications, setNotifications] = useState([]);
  const [shownNotifications, setShownNotifications] = useState([]);

  useEffect(() => {
    // Load Firebase and XLSX scripts
    const loadScript = (src, id, onReady) => {
        if (document.getElementById(id)) {
            onReady();
            return;
        }
        const script = document.createElement('script');
        script.src = src;
        script.id = id;
        script.async = true;
        script.onload = onReady;
        document.body.appendChild(script);
    };

    let firebaseScriptsLoaded = 0;
    const firebaseScriptUrls = [
        "https://www.gstatic.com/firebasejs/8.10.1/firebase-app.js",
        "https://www.gstatic.com/firebasejs/8.10.1/firebase-auth.js",
        "https://www.gstatic.com/firebasejs/8.10.1/firebase-firestore.js"
    ];

    const onFirebaseScriptLoad = () => {
        firebaseScriptsLoaded++;
        if (firebaseScriptsLoaded === firebaseScriptUrls.length) {
            setSdkReady(true);
        }
    };
    
    if (window.firebase) {
        setSdkReady(true);
    } else {
        firebaseScriptUrls.forEach((url, index) => loadScript(url, `firebase-sdk-${index}`, onFirebaseScriptLoad));
    }
    
    if(window.XLSX) {
        setXlsxReady(true);
    } else {
        loadScript("https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js", "xlsx-sdk", () => setXlsxReady(true));
    }
  }, []);
  
  const refetchData = useCallback(async (db, user, dashboardId) => {
    if (!user || !db || !dashboardId) {
        setProjects([]);
        setResources([]);
        setTasks([]);
        setIsDataLoading(false);
        return;
    }
    setIsDataLoading(true);
    try {
        const userRef = db.collection('users').doc(user.uid);
        const qProjects = userRef.collection("projects").where("dashboardId", "==", dashboardId);
        const qResources = userRef.collection("resources").where("dashboardId", "==", dashboardId);
        const qTasks = userRef.collection("tasks").where("dashboardId", "==", dashboardId);
        const settingsDoc = await userRef.collection("userSettings").doc(dashboardId).get();

        if (settingsDoc.exists) {
            const settings = settingsDoc.data();
            setProjectSortBy(settings.projectSortBy || 'default');
            setTaskSortBy(settings.taskSortBy || 'startDate');
            setTaskStatusFilter(settings.taskStatusFilter || 'All');
            setHolidays(settings.holidays || []);
        } else {
            setHolidays([]);
        }
        
        const [projectSnap, resourceSnap, taskSnap] = await Promise.all([
            qProjects.get(),
            qResources.get(),
            qTasks.get()
        ]);

        setProjects(projectSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        setResources(resourceSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        
        const processedTasks = taskSnap.docs.map(doc => {
            const data = doc.data();
            if (data.resourceIds && !data.assignedResources) {
                const numResources = data.resourceIds.length;
                const weight = numResources > 0 ? 100 / numResources : 100;
                data.assignedResources = data.resourceIds.map(id => ({ resourceId: id, weight: weight }));
            }
            return { id: doc.id, ...data };
        });
        setTasks(processedTasks);

    } catch (error) {
        console.error("Error fetching data:", error);
    } finally {
        setIsDataLoading(false);
    }
  }, []);

  useEffect(() => {
    if (sdkReady) {
        try {
            const app = window.firebase.initializeApp(firebaseConfig);
            const auth = app.auth();
            const db = app.firestore();
            setFirebaseServices({ auth, db });

            const unsubscribe = auth.onAuthStateChanged(async (user) => {
                if (user) {
                    const dashboardQuery = db.collection('users').doc(user.uid).collection('dashboards');
                    const dashboardSnap = await dashboardQuery.get();
                    let userDashboards = dashboardSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                    
                    if (userDashboards.length === 0) {
                        const newDashboardRef = db.collection('users').doc(user.uid).collection('dashboards').doc();
                        const defaultDashboard = { id: newDashboardRef.id, name: 'My First Dashboard' };
                        await newDashboardRef.set(defaultDashboard);
                        userDashboards = [defaultDashboard];
                    }
                    
                    setUser(user);
                    setDashboards(userDashboards);
                    setActiveDashboardId(userDashboards[0].id);
                } else {
                    setUser(null);
                    setDashboards([]);
                    setActiveDashboardId(null);
                }
                setIsAuthReady(true);
            });
            return unsubscribe;
        } catch (error) {
            if (error.code === 'duplicate-app') {
                 const app = window.firebase.app();
                 const auth = app.auth();
                 const db = app.firestore();
                 setFirebaseServices({ auth, db });
                 const unsubscribe = auth.onAuthStateChanged(async (user) => {
                    if (user) {
                        const dashboardQuery = db.collection('users').doc(user.uid).collection('dashboards');
                        const dashboardSnap = await dashboardQuery.get();
                        let userDashboards = dashboardSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                        
                        if (userDashboards.length === 0) {
                            const newDashboardRef = db.collection('users').doc(user.uid).collection('dashboards').doc();
                            const defaultDashboard = { id: newDashboardRef.id, name: 'My First Dashboard' };
                            await newDashboardRef.set(defaultDashboard);
                            userDashboards = [defaultDashboard];
                        }
                        
                        setUser(user);
                        setDashboards(userDashboards);
                        setActiveDashboardId(userDashboards[0].id);
                    } else {
                        setUser(null);
                        setDashboards([]);
                        setActiveDashboardId(null);
                    }
                    setIsAuthReady(true);
                });
                return unsubscribe;
            }
            console.error("Firebase initialization error:", error);
            setIsAuthReady(true); // Ensure app doesn't hang on error
        }
    }
  }, [sdkReady]);

  useEffect(() => {
      if (isAuthReady && user && activeDashboardId) {
          refetchData(firebaseServices.db, user, activeDashboardId);
      } else if (isAuthReady && !user) {
          setIsDataLoading(false);
      }
  }, [isAuthReady, user, activeDashboardId, firebaseServices.db, refetchData]);

  useEffect(() => {
    const interval = setInterval(() => {
        const now = new Date();
        const todayStr = now.toISOString().split('T')[0];
        const newNotifications = [];

        tasks.forEach(task => {
            if (task.reminder === 'none') return;
            
            let isDue = false;
            let notificationId = '';

            if (task.reminder === 'specific') {
                if (task.specificReminderDateTime) {
                    const reminderTime = new Date(task.specificReminderDateTime);
                    notificationId = `${task.id}-${task.specificReminderDateTime}`;
                    
                    const reminderDateStr = reminderTime.toISOString().split('T')[0];
                    if (todayStr === reminderDateStr && now >= reminderTime) {
                        isDue = true;
                    }
                }
            } else {
                const startDate = new Date(task.startDate);
                const endDate = new Date(task.endDate);
                if (now < startDate || now > endDate) return;
                
                switch (task.reminder) {
                    case 'daily':
                        notificationId = `${task.id}-${todayStr}`;
                        isDue = true;
                        break;
                    case 'weekly':
                        if (now.getDay() === startDate.getDay()) {
                            notificationId = `${task.id}-${todayStr}`;
                            isDue = true;
                        }
                        break;
                    case 'monthly':
                        if (now.getDate() === startDate.getDate()) {
                            notificationId = `${task.id}-${todayStr}`;
                            isDue = true;
                        }
                        break;
                    default:
                        break;
                }
            }

            if (isDue && !shownNotifications.includes(notificationId)) {
                newNotifications.push({
                    id: notificationId,
                    title: `Reminder: ${task.name}`,
                    message: `Task is due. Project: ${projects.find(p => p.id === task.projectId)?.name || 'N/A'}`
                });
            }
        });

        if (newNotifications.length > 0) {
            setNotifications(prev => [...prev, ...newNotifications]);
            setShownNotifications(prev => [...prev, ...newNotifications.map(n => n.id)]);
        }
    }, 10000); // Check every 10 seconds

    return () => clearInterval(interval);
}, [tasks, projects, shownNotifications]);
  
  const toggleDarkMode = () => {
      const newMode = !darkMode;
      setDarkMode(newMode);
      if (newMode) {
          document.documentElement.classList.add('dark');
      } else {
          document.documentElement.classList.remove('dark');
      }
  };

  const handleOpenModal = (type, data = null) => {
    setModalConfig({ type, data });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => setIsModalOpen(false);

  const handleSave = async (item) => {
    if (!user || !firebaseServices.db || !activeDashboardId) return;
    const { db } = firebaseServices;
    let { id, ...data } = item;
    data.dashboardId = activeDashboardId;
    
    const type = modalConfig.type;
    const collectionName = `${type.toLowerCase()}s`;
    const userRef = db.collection('users').doc(user.uid);

    try {
        if (type === 'Task') {
            const assignedResources = data.assignedResources || [];
            if (data.startDate && data.endDate) {
                const resourceLeaves = assignedResources.flatMap(ar => {
                    const resource = resources.find(r => r.id === ar.resourceId);
                    return resource ? resource.leave : [];
                });
                data.hours = getWorkingDays(data.startDate, data.endDate, resourceLeaves, holidays) * DAILY_WORK_HOURS;
            }
            delete data.resourceIds;
        }

        if (id) { // Update
            await userRef.collection(collectionName).doc(id).update(data);
            if (type === 'Project') setProjects(prev => prev.map(p => p.id === id ? {id, ...data} : p));
            else if (type === 'Resource') setResources(prev => prev.map(r => r.id === id ? {id, ...data} : r));
            else if (type === 'Task') setTasks(prev => prev.map(t => t.id === id ? {id, ...data} : t));
        } else { // Add
            if (type === 'Project') {
                data.assignedResources = [];
                data.priority = data.priority || 'Medium';
                data.tshirtSize = data.tshirtSize || 'M';
            }
            if (type === 'Task') {
                data.reminder = data.reminder || 'none';
                data.priority = data.priority || 'Medium';
                data.status = data.status || 'TODO';
            }
            const docRef = await userRef.collection(collectionName).add(data);
            const newItem = {id: docRef.id, ...data};
            if (type === 'Project') setProjects(prev => [...prev, newItem]);
            else if (type === 'Resource') setResources(prev => [...prev, newItem]);
            else if (type === 'Task') setTasks(prev => [...prev, newItem]);
        }
    } catch (error) {
        console.error("Error saving document: ", error);
    }
    
    handleCloseModal();
  };
  
  const handleDelete = async (type, id) => {
    if (!user || !firebaseServices.db) return;
    const { db } = firebaseServices;
    
    const collectionName = `${type.toLowerCase()}s`;
    await db.collection('users').doc(user.uid).collection(collectionName).doc(id).delete();

    if (type === 'Project') {
        setProjects(prev => prev.filter(p => p.id !== id));
        setTasks(prev => prev.filter(t => t.projectId !== id));
    } else if (type === 'Resource') {
        setResources(prev => prev.filter(r => r.id !== id));
        setTasks(prev => prev.map(t => {
            if (t.assignedResources && t.assignedResources.some(ar => ar.resourceId === id)) {
                return { ...t, assignedResources: t.assignedResources.filter(ar => ar.resourceId !== id) };
            }
            return t;
        }));
        setProjects(prev => prev.map(p => {
             if (p.assignedResources && p.assignedResources.includes(id)) {
                return { ...p, assignedResources: p.assignedResources.filter(resId => resId !== id) };
            }
            return p;
        }));
    } else if (type === 'Task') {
        setTasks(prev => prev.filter(t => t.id !== id));
    }
  };

  const handleUpdateAssignments = async (projectId, assignedResourceIds) => {
      const { db } = firebaseServices;
      await db.collection('users').doc(user.uid).collection("projects").doc(projectId).update({ assignedResources: assignedResourceIds });
      setProjects(prev => prev.map(p => p.id === projectId ? { ...p, assignedResources: assignedResourceIds } : p));
  };

  const handleUpdateTaskDates = async (taskId, newStartDate, newEndDate) => {
      const { db } = firebaseServices;
      await db.collection('users').doc(user.uid).collection("tasks").doc(taskId).update({ startDate: newStartDate, endDate: newEndDate });
      setTasks(prev => prev.map(task => task.id === taskId ? { ...task, startDate: newStartDate, endDate: newEndDate } : task));
  };
  
  const handleSignOut = async () => {
      if(firebaseServices.auth) {
        await firebaseServices.auth.signOut();
      }
  };

  const handleSortChange = async (type, value) => {
      if (type === 'project') setProjectSortBy(value);
      else if (type === 'task') setTaskSortBy(value);
      else if (type === 'taskStatus') setTaskStatusFilter(value);

      if (user && firebaseServices.db && activeDashboardId) {
          const { db } = firebaseServices;
          const settingsRef = db.collection('users').doc(user.uid).collection('userSettings').doc(activeDashboardId);
          await settingsRef.set({
              projectSortBy: type === 'project' ? value : projectSortBy,
              taskSortBy: type === 'task' ? value : taskSortBy,
              taskStatusFilter: type === 'taskStatus' ? value : taskStatusFilter,
          }, { merge: true });
      }
  }
  
  const handleSaveHolidays = async (newHolidays) => {
      if (user && firebaseServices.db && activeDashboardId) {
          const { db } = firebaseServices;
          const settingsRef = db.collection('users').doc(user.uid).collection('userSettings').doc(activeDashboardId);
          await settingsRef.set({ holidays: newHolidays }, { merge: true });
          setHolidays(newHolidays);
      }
  };
  
  const handleClearDashboard = async () => {
    if (!user || !firebaseServices.db || !activeDashboardId) return;
    setIsDataLoading(true);
    const { db } = firebaseServices;
    const collectionsToClear = ['projects', 'resources', 'tasks'];
    const batch = db.batch();
    const userRef = db.collection('users').doc(user.uid);

    for (const collectionName of collectionsToClear) {
        const snapshot = await userRef.collection(collectionName)
            .where("dashboardId", "==", activeDashboardId)
            .get();
        snapshot.forEach(doc => batch.delete(doc.ref));
    }
    
    await batch.commit();
    await refetchData(db, user, activeDashboardId);
  };

  const handleCreateDashboard = async (name) => {
    if (!user || !firebaseServices.db || !name) return;
    const { db } = firebaseServices;
    const newDashboardRef = db.collection('users').doc(user.uid).collection('dashboards').doc();
    const newDashboard = { id: newDashboardRef.id, name };
    await newDashboardRef.set(newDashboard);
    setDashboards(prev => [...prev, newDashboard]);
    setActiveDashboardId(newDashboard.id);
    setIsDashboardModalOpen(false);
  };

  const handleDeleteDashboard = (dashboardId, dashboardName) => {
    if (dashboards.length <= 1) {
        // This should ideally be a more user-friendly notification.
        console.error("You cannot delete your only dashboard.");
        return;
    }

    const confirmDeletion = async () => {
        setIsConfirmModalOpen(false);
        const { db } = firebaseServices;
        const userRef = db.collection('users').doc(user.uid);
        
        const collectionsToClear = ['projects', 'resources', 'tasks', 'userSettings'];
        const deleteDataBatch = db.batch();
        for (const collectionName of collectionsToClear) {
            if (collectionName === 'userSettings') {
                const docToDelete = userRef.collection(collectionName).doc(dashboardId);
                deleteDataBatch.delete(docToDelete);
            } else {
                const snapshot = await userRef.collection(collectionName).where("dashboardId", "==", dashboardId).get();
                snapshot.forEach(doc => deleteDataBatch.delete(doc.ref));
            }
        }
        await deleteDataBatch.commit();
        
        await userRef.collection('dashboards').doc(dashboardId).delete();
        
        const newDashboards = dashboards.filter(d => d.id !== dashboardId);
        setDashboards(newDashboards);
        
        const newActiveId = newDashboards[0].id;
        setActiveDashboardId(newActiveId);
    };

    setConfirmModalConfig({
        onConfirm: confirmDeletion,
        title: 'Delete Dashboard?',
        message: `Are you sure you want to delete the dashboard "${dashboardName}" and all its data? This action cannot be undone.`
    });
    setIsConfirmModalOpen(true);
  };

  const handleRenameDashboard = (dashboardId, currentName) => {
      setRenameModalConfig({ id: dashboardId, name: currentName });
      setIsRenameModalOpen(true);
  };

  const handleSaveRename = async (newName) => {
      if (!newName || !renameModalConfig.id) return;
      const { db } = firebaseServices;
      const userRef = db.collection('users').doc(user.uid);
      await userRef.collection('dashboards').doc(renameModalConfig.id).update({ name: newName });
      setDashboards(dashboards.map(d => d.id === renameModalConfig.id ? { ...d, name: newName } : d));
      setIsRenameModalOpen(false);
  };

  const handleCopyDashboard = async (dashboardId) => {
    setIsDataLoading(true);
    const { db } = firebaseServices;
    const userRef = db.collection('users').doc(user.uid);
    const originalDashboard = dashboards.find(d => d.id === dashboardId);
    if (!originalDashboard) {
        setIsDataLoading(false);
        return;
    }

    // 1. Create new dashboard
    const newDashboardRef = userRef.collection('dashboards').doc();
    const newDashboard = { id: newDashboardRef.id, name: `${originalDashboard.name} - Copy` };
    await newDashboardRef.set(newDashboard);

    const batch = db.batch();
    const resourceIdMap = new Map();
    const projectIdMap = new Map();

    // 2. Copy resources
    const resourcesSnap = await userRef.collection('resources').where('dashboardId', '==', dashboardId).get();
    resourcesSnap.forEach(doc => {
        const oldId = doc.id;
        const data = doc.data();
        const newResRef = userRef.collection('resources').doc();
        resourceIdMap.set(oldId, newResRef.id);
        batch.set(newResRef, { ...data, dashboardId: newDashboard.id });
    });

    // 3. Copy projects
    const projectsSnap = await userRef.collection('projects').where('dashboardId', '==', dashboardId).get();
    projectsSnap.forEach(doc => {
        const oldId = doc.id;
        const data = doc.data();
        const newProjRef = userRef.collection('projects').doc();
        projectIdMap.set(oldId, newProjRef.id);
        const newAssignedResources = (data.assignedResources || []).map(resId => resourceIdMap.get(resId)).filter(Boolean);
        batch.set(newProjRef, { ...data, dashboardId: newDashboard.id, assignedResources: newAssignedResources });
    });

    // 4. Copy tasks
    const tasksSnap = await userRef.collection('tasks').where('dashboardId', '==', dashboardId).get();
    tasksSnap.forEach(doc => {
        const data = doc.data();
        const newTaskRef = userRef.collection('tasks').doc();
        const newProjectId = projectIdMap.get(data.projectId);
        const newAssignedResources = (data.assignedResources || []).map(ar => ({
            ...ar,
            resourceId: resourceIdMap.get(ar.resourceId)
        })).filter(ar => ar.resourceId);

        if (newProjectId) {
            batch.set(newTaskRef, { ...data, dashboardId: newDashboard.id, projectId: newProjectId, assignedResources: newAssignedResources });
        }
    });

    await batch.commit();
    setDashboards(prev => [...prev, newDashboard]);
    setActiveDashboardId(newDashboard.id);
    setIsDataLoading(false);
  };

  const handleSwitchDashboard = async (dashboardId) => {
    if (dashboardId === activeDashboardId) return;
    setActiveDashboardId(dashboardId);
  };

  const handleExportJson = () => {
    const activeDashboard = dashboards.find(d => d.id === activeDashboardId);
    const dataToExport = {
      dashboardName: activeDashboard?.name,
      projects: projects.map(({ id, name, priority, tshirtSize, startDate, endDate, assignedResources, dashboardId }) => ({ id, name, priority, tshirtSize, startDate, endDate, assignedResources, dashboardId })),
      resources: resources.map(({ id, name, role, leave, dashboardId }) => ({ id, name, role, leave, dashboardId })),
      tasks: tasks.map(({ id, name, priority, status, startDate, endDate, reminder, specificReminderDateTime, projectId, assignedResources, dashboardId }) => ({ id, name, priority, status, startDate, endDate, reminder, specificReminderDateTime, projectId, assignedResources, dashboardId })),
      holidays,
    };
    const jsonString = `data:text/json;charset=utf-8,${encodeURIComponent(
      JSON.stringify(dataToExport, null, 2)
    )}`;
    const link = document.createElement("a");
    link.href = jsonString;
    link.download = `pmp-export-${activeDashboard?.name.replace(/\s/g, '_') || 'dashboard'}-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    setIsImportExportModalOpen(false);
  };

  const handleExportExcel = () => {
    if (!xlsxReady) return;
    const activeDashboard = dashboards.find(d => d.id === activeDashboardId);
    const wb = window.XLSX.utils.book_new();
    
    const projectsSheet = window.XLSX.utils.json_to_sheet(projects.map(({ id, name, priority, tshirtSize, startDate, endDate, assignedResources, dashboardId }) => ({ 
        id, name, priority, tshirtSize, startDate, endDate, assignedResources: (assignedResources || []).join(', '), dashboardId
    })));
    window.XLSX.utils.book_append_sheet(wb, projectsSheet, "Projects");

    const resourcesSheet = window.XLSX.utils.json_to_sheet(resources.map(({ id, name, role, leave, dashboardId }) => ({ 
        id, name, role, leave: (leave || []).map(l => `${l.startDate} to ${l.endDate}`).join('; '), dashboardId
    })));
    window.XLSX.utils.book_append_sheet(wb, resourcesSheet, "Resources");

    const tasksSheetData = tasks.map(t => {
        const project = projects.find(p => p.id === t.projectId);
        const assignedResourcesStr = (t.assignedResources || []).map(ar => `${ar.resourceId}:${ar.weight}%`).join(', ');
        return {
            id: t.id, name: t.name, priority: t.priority, status: t.status, startDate: t.startDate, endDate: t.endDate,
            reminder: t.reminder, specificReminderDateTime: t.specificReminderDateTime,
            projectName: project ? project.name : 'N/A', projectId: t.projectId, assignedResources: assignedResourcesStr, dashboardId: t.dashboardId
        }
    });
    const tasksSheet = window.XLSX.utils.json_to_sheet(tasksSheetData);
    window.XLSX.utils.book_append_sheet(wb, tasksSheet, "Tasks");

    window.XLSX.writeFile(wb, `pmp-export-${activeDashboard?.name.replace(/\s/g, '_') || 'dashboard'}-${new Date().toISOString().split('T')[0]}.xlsx`);
    setIsImportExportModalOpen(false);
  };

  const handleExportCsv = () => {
      if (!xlsxReady) return;
      const activeDashboard = dashboards.find(d => d.id === activeDashboardId);
      const tasksSheetData = tasks.map(t => {
        const project = projects.find(p => p.id === t.projectId);
        const resourceNames = (t.assignedResources || []).map(ar => {
            const res = resources.find(r => r.id === ar.resourceId);
            return res ? `${res.name} (${ar.weight}%)` : 'Unknown';
        }).join('; ');
        return {
            taskName: t.name, projectName: project ? project.name : 'N/A', resourceNames: resourceNames,
            startDate: t.startDate, endDate: t.endDate, status: t.status, priority: t.priority,
        }
    });
    const tasksSheet = window.XLSX.utils.json_to_sheet(tasksSheetData);
    const csvString = window.XLSX.utils.sheet_to_csv(tasksSheet);
    
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `pmp-tasks-export-${activeDashboard?.name.replace(/\s/g, '_') || 'dashboard'}-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setIsImportExportModalOpen(false);
  };

  const handleImport = async (data, type) => {
    if (!user || !firebaseServices.db || !activeDashboardId) return;
    
    await handleClearDashboard(); // Clear current dashboard first
    setIsDataLoading(true);
    const { db } = firebaseServices;
    const userRef = db.collection('users').doc(user.uid);

    try {
      const importBatch = db.batch();
      let importedProjects, importedResources, importedTasks, importedHolidays;

      if (type === 'json') {
          ({ projects: importedProjects, resources: importedResources, tasks: importedTasks, holidays: importedHolidays } = data);
      } else if (type === 'excel' || type === 'csv') {
          importedProjects = data.Projects || [];
          importedResources = data.Resources || [];
          importedTasks = data.Tasks || [];
          importedHolidays = data.Holidays || [];
      }

      const resourceIdMap = new Map();
      if (importedResources && Array.isArray(importedResources)) {
        importedResources.forEach(res => {
            const oldId = res.id;
            const { id, ...resData } = res;
            if (!res.name) return; 
            const newResRef = userRef.collection('resources').doc();
            resourceIdMap.set(oldId, newResRef.id);
            if (typeof resData.leave === 'string' && resData.leave.length > 0) {
                resData.leave = resData.leave.split(';').map(pStr => { const [s, e] = pStr.split(' to '); return { startDate: s, endDate: e }; });
            } else { resData.leave = resData.leave || []; }
            importBatch.set(newResRef, { ...resData, dashboardId: activeDashboardId });
        });
      }

      const projectIdMap = new Map();
      if (importedProjects && Array.isArray(importedProjects)) {
        importedProjects.forEach(proj => {
            const oldId = proj.id;
            const { id, assignedResources, ...projData } = proj;
            if (!proj.name) return; 
            const newProjRef = userRef.collection('projects').doc();
            projectIdMap.set(oldId, newProjRef.id);
            let newAssignedResources = (typeof assignedResources === 'string' ? assignedResources.split(',') : (assignedResources || []))
                .map(resId => resourceIdMap.get(resId.trim())).filter(Boolean);
            importBatch.set(newProjRef, { ...projData, dashboardId: activeDashboardId, assignedResources: newAssignedResources });
        });
      }
      
      if (importedTasks && Array.isArray(importedTasks)) {
        importedTasks.forEach(task => {
            const { id, projectId, assignedResources, hours, ...taskData } = task;
            if (!task.name) return;
            const newTaskRef = userRef.collection('tasks').doc();
            let newAssignedResources = [];
            if (typeof assignedResources === 'string') {
                newAssignedResources = assignedResources.split(',').map(s => { const [r, w] = s.split(':'); return { resourceId: resourceIdMap.get(r.trim()), weight: parseFloat(w) || 0 }; }).filter(ar => ar.resourceId);
            } else if (Array.isArray(assignedResources)) {
                newAssignedResources = assignedResources.map(ar => ({ resourceId: resourceIdMap.get(ar.resourceId), weight: ar.weight })).filter(ar => ar.resourceId);
            }
            const resourceLeaves = newAssignedResources.flatMap(ar => importedResources.find(r => resourceIdMap.get(r.id) === ar.resourceId)?.leave || []);
            const calculatedHours = getWorkingDays(task.startDate, task.endDate, resourceLeaves, holidays) * DAILY_WORK_HOURS;
            importBatch.set(newTaskRef, { ...taskData, projectId: projectIdMap.get(projectId) || null, assignedResources: newAssignedResources, hours: calculatedHours, dashboardId: activeDashboardId });
        });
      }
      
      await importBatch.commit();
      if (importedHolidays) await handleSaveHolidays(Array.isArray(importedHolidays) ? importedHolidays.map(h => h.date || h) : []);
      
    } catch (error) {
        console.error("Import failed:", error);
    } finally {
      await refetchData(db, user, activeDashboardId);
      setIsImportExportModalOpen(false);
    }
  };

  const timelineRange = useMemo(() => {
    const allDates = [...projects.flatMap(p => [new Date(p.startDate), new Date(p.endDate)]), ...tasks.flatMap(t => [new Date(t.startDate), new Date(t.endDate)])];
    const validDates = allDates.filter(d => !isNaN(d.getTime()));
    if(validDates.length === 0) {
        const today = new Date();
        const nextMonth = new Date();
        nextMonth.setMonth(today.getMonth() + 1);
        return { timelineStart: today.toISOString().split('T')[0], totalDays: 30 };
    }
    const minDate = new Date(Math.min(...validDates));
    const maxDate = new Date(Math.max(...validDates));
    const timelineStart = new Date(minDate);
    timelineStart.setDate(minDate.getDate() - 5);
    const timelineEnd = new Date(maxDate);
    timelineEnd.setDate(maxDate.getDate() + 5);
    return { timelineStart: timelineStart.toISOString().split('T')[0], totalDays: dayDiff(timelineStart, timelineEnd) + 1 };
  }, [projects, tasks]);
  
  const dismissNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  if (!isAuthReady || !sdkReady) {
      return <div className="flex items-center justify-center min-h-screen dark:bg-gray-900 dark:text-white">Loading Application...</div>;
  }
  
  if (!user) {
      return <AuthScreen auth={firebaseServices.auth} />;
  }

  return (
    <div className={darkMode ? 'dark' : ''}>
      <NotificationContainer notifications={notifications} onDismiss={dismissNotification} />
      <style>{`
        @keyframes fade-in-right { from { opacity: 0; transform: translateX(100%); } to { opacity: 1; transform: translateX(0); } }
        .animate-fade-in-right { animation: fade-in-right 0.5s ease-out forwards; }
      `}</style>
      <div className="bg-gray-100 dark:bg-gray-900 min-h-screen text-gray-900 dark:text-gray-100 font-sans transition-colors duration-300">
        <header className="bg-white dark:bg-gray-800 shadow-md p-4 flex flex-col sm:flex-row justify-between items-center sticky top-0 z-40 gap-4">
          <div className="flex items-center gap-4">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 dark:text-white">QPM</h1>
            <DashboardSwitcher dashboards={dashboards} activeDashboardId={activeDashboardId} onSwitch={handleSwitchDashboard} onCreate={() => setIsDashboardModalOpen(true)} onDelete={handleDeleteDashboard} onRename={handleRenameDashboard} onCopy={handleCopyDashboard} />
          </div>
          <div className="flex items-center gap-2 md:gap-4 flex-wrap justify-center">
            <button onClick={() => handleOpenModal('Project')} className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"><Plus size={16} /> Project</button>
            <button onClick={() => handleOpenModal('Resource')} className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"><Plus size={16} /> Resource</button>
            <button onClick={() => handleOpenModal('Task')} className="flex items-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm"><Plus size={16} /> Task</button>
            <button onClick={() => setIsImportExportModalOpen(true)} className="p-2 rounded-full bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600"><Download size={18}/></button>
            <button onClick={() => setIsHolidaysModalOpen(true)} className="p-2 rounded-full bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600"><CalendarDays size={18}/></button>
            <button onClick={toggleDarkMode} className="p-2 rounded-full bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600">{darkMode ? '☀️' : '🌙'}</button>
            <button onClick={() => setIsProfileModalOpen(true)} className="p-2 rounded-full bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600"><UserIcon size={18}/></button>
            <button onClick={handleSignOut} className="p-2 rounded-full bg-red-500 text-white hover:bg-red-600"><LogOut size={18}/></button>
          </div>
        </header>

        <nav className="bg-white dark:bg-gray-800 shadow-sm">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-center h-16">
                    <div className="flex space-x-4 sm:space-x-8">
                        <button onClick={() => setActiveTab('home')} className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${activeTab === 'home' ? 'border-blue-500 text-gray-900 dark:text-white' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:hover:text-gray-300'}`}>
                            <Home className="mr-2 h-5 w-5"/> Home
                        </button>
                        <button onClick={() => setActiveTab('projects')} className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${activeTab === 'projects' ? 'border-blue-500 text-gray-900 dark:text-white' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:hover:text-gray-300'}`}>
                            <GanttChartSquare className="mr-2 h-5 w-5"/> Projects
                        </button>
                        <button onClick={() => setActiveTab('resources')} className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${activeTab === 'resources' ? 'border-blue-500 text-gray-900 dark:text-white' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:hover:text-gray-300'}`}>
                            <UserCog className="mr-2 h-5 w-5"/> Resources
                        </button>
                         <button onClick={() => setActiveTab('tasks')} className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${activeTab === 'tasks' ? 'border-blue-500 text-gray-900 dark:text-white' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:hover:text-gray-300'}`}>
                            <ListTodo className="mr-2 h-5 w-5"/> Tasks
                        </button>
                    </div>
                </div>
            </div>
        </nav>

        <main className="p-4 md:p-8">
            { isDataLoading ? <div className="text-center">Loading Dashboard Data...</div> : (
            <>
            {activeTab === 'home' && (
                <div className="space-y-8">
                    <CalendarView tasks={tasks} resources={resources} onUpdateTaskDates={handleUpdateTaskDates} onOpenModal={handleOpenModal} />
                    <ProjectTimeline projects={projects} tasks={tasks} resources={resources} timelineRange={timelineRange} onEdit={handleOpenModal} onDelete={handleDelete} sortBy={projectSortBy} setSortBy={(val) => handleSortChange('project', val)} />
                    <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                        <div className="lg:col-span-3 space-y-8">
                            <ResourceManagement resources={resources} tasks={tasks} projects={projects} onEdit={handleOpenModal} onDelete={handleDelete} holidays={holidays} />
                            <ProjectStaffingAnalysis projects={projects} resources={resources} tasks={tasks} holidays={holidays} />
                        </div>
                        <div className="lg:col-span-2 space-y-8">
                            <ProjectResourceAssignment projects={projects} resources={resources} onUpdateAssignments={handleUpdateAssignments} />
                            <TaskList tasks={tasks} projects={projects} resources={resources} onEdit={handleOpenModal} onDelete={handleDelete} sortBy={taskSortBy} setSortBy={(val) => handleSortChange('task', val)} statusFilter={taskStatusFilter} setStatusFilter={(val) => handleSortChange('taskStatus', val)} projectFilter={taskProjectFilter} setProjectFilter={setTaskProjectFilter} resourceFilter={taskResourceFilter} setResourceFilter={setTaskResourceFilter} />
                            <Reminders tasks={tasks} projects={projects} />
                        </div>
                    </div>
                </div>
            )}
            {activeTab === 'projects' && (
                <div className="space-y-8">
                    <ProjectTimeline projects={projects} tasks={tasks} resources={resources} timelineRange={timelineRange} onEdit={handleOpenModal} onDelete={handleDelete} sortBy={projectSortBy} setSortBy={(val) => handleSortChange('project', val)} />
                    <ProjectStaffingAnalysis projects={projects} resources={resources} tasks={tasks} holidays={holidays} />
                </div>
            )}
            {activeTab === 'resources' && (
                <div className="space-y-8">
                    <ResourceManagement resources={resources} tasks={tasks} projects={projects} onEdit={handleOpenModal} onDelete={handleDelete} holidays={holidays} />
                    <ProjectResourceAssignment projects={projects} resources={resources} onUpdateAssignments={handleUpdateAssignments} />
                </div>
            )}
            {activeTab === 'tasks' && (
                <div className="space-y-8">
                    <CalendarView tasks={tasks} resources={resources} onUpdateTaskDates={handleUpdateTaskDates} onOpenModal={handleOpenModal} />
                     <TaskList tasks={tasks} projects={projects} resources={resources} onEdit={handleOpenModal} onDelete={handleDelete} sortBy={taskSortBy} setSortBy={(val) => handleSortChange('task', val)} statusFilter={taskStatusFilter} setStatusFilter={(val) => handleSortChange('taskStatus', val)} projectFilter={taskProjectFilter} setProjectFilter={setTaskProjectFilter} resourceFilter={taskResourceFilter} setResourceFilter={setTaskResourceFilter} />
                     <Reminders tasks={tasks} projects={projects} />
                </div>
            )}
            </>
            )}
        </main>
    
        {isModalOpen && (
          <Modal onClose={handleCloseModal}>
            <DataForm type={modalConfig.type} onSave={handleSave} onCancel={handleCloseModal} initialData={modalConfig.data} projects={projects} resources={resources} />
          </Modal>
        )}
        {isProfileModalOpen && (
            <ProfileModal auth={firebaseServices.auth} onClose={() => setIsProfileModalOpen(false)} onClearDashboard={handleClearDashboard} />
        )}
         {isHolidaysModalOpen && (
            <HolidaysModal holidays={holidays} onSave={handleSaveHolidays} onClose={() => setIsHolidaysModalOpen(false)} />
        )}
        {isImportExportModalOpen && (
            <ImportExportModal 
                onClose={() => setIsImportExportModalOpen(false)}
                onExportJson={handleExportJson}
                onExportExcel={handleExportExcel}
                onExportCsv={handleExportCsv}
                onImport={handleImport}
                xlsxReady={xlsxReady}
            />
        )}
        {isDashboardModalOpen && (
            <DashboardModal onClose={() => setIsDashboardModalOpen(false)} onSave={handleCreateDashboard} title="Create New Dashboard" />
        )}
        {isRenameModalOpen && (
            <DashboardModal 
                onClose={() => setIsRenameModalOpen(false)} 
                onSave={handleSaveRename} 
                initialName={renameModalConfig.name}
                title="Rename Dashboard"
            />
        )}
        {isConfirmModalOpen && (
            <ConfirmationModal
                isOpen={isConfirmModalOpen}
                onClose={() => setIsConfirmModalOpen(false)}
                onConfirm={confirmModalConfig.onConfirm}
                title={confirmModalConfig.title}
                message={confirmModalConfig.message}
            />
        )}
      </div>
    </div>
  );
}
